import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import InsightsIcon from '@mui/icons-material/Insights';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { CompositeDataPicker, type HydratedData, type PickerConfig } from '@modules/data-pickers';
import {
  useTaskStore,
  useTaskTransition,
  isTerminalTaskStatus,
  ACTIVE_STATUSES,
  type BackendTask,
} from '@modules/background-tasks';
import { AvailableImageModelSelector, AvailableModelSelector } from '@modules/ai-settings';
import { SDialog, StyledTextField } from '@modules/ui-components';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';
import { useProjectDefaultModel } from '@modules/content-plan';
import { ProjectLocalesProvider } from '@modules/locale-config';
import { GenerationHistoryList } from '@modules/content-plan/components/generations/GenerationHistoryList';
import { GenerationReviewDialog } from '@modules/content-plan/components/generations/GenerationReviewDialog';
import { useGenerationRuns, type GenerationRunSummary } from '@modules/content-plan/hooks/useGenerationRuns';
import type { GenerationDraft } from '@modules/content-plan/hooks/useGenerationDrafts';
import type {
  AdCreativeBrief,
  AdCreativeDesignCollection,
  AdCreativeDesignPreset,
  AdCreativeGenerationRun,
  AdCreativeItem,
  AdCreativePlan,
  AdCreativeTemplate,
  AdNetwork,
} from '@segmently/schema-models/ad-creative';
import type { AiAssetExperiment } from '@segmently/schema-models/content-plan';
import {
  AdCreativeApiClient,
  type AdCreativeCampaign,
  type AdCreativeMetaDraft,
  type MetaAdsAdAccountSummary,
  type MetaAdsAdSetSummary,
  type MetaAdsCampaignSummary,
  type MetaAdsConnectionStatus,
  type MetaAdsPageSummary,
  type AdCreativeResearchRun,
  type CreateAdCreativeBriefRequest,
  type AdCreativeTaskDocument,
  type SaveAdCreativeDesignCollectionRequest,
} from '../services/AdCreativeApiClient';
import { AD_CREATIVE } from '../selectors';
import { AdCreativeVideoSection, isVideoCreativeItem } from '../components/AdCreativeVideoSection';
import { AdCreativeBatchWorkspace } from '../components/batch/AdCreativeBatchWorkspace';
import { StoryboardWorkspace } from '../components/storyboard/StoryboardWorkspace';
import { readBatchStep, type BatchStepKey } from '../components/batch/batchModel';
import { AdCreativeCreateHome } from '../components/home/AdCreativeCreateHome';
import { BrandStylesPage } from '../components/brand/BrandStylesPage';
import { AdCreativeLibraryPage } from '../components/library/AdCreativeLibraryPage';
import { parseLocalizedVariationHandoff } from '../components/library/localizedVariationHandoff';
import { MetaImportRefreshPage } from '../components/importRefresh/MetaImportRefreshPage';
import { CreativeDecisionRefreshDraftPage } from '../components/importRefresh/CreativeDecisionRefreshDraftPage';
import { parseCreativeDecisionRefreshHandoff } from '../components/importRefresh/creativeDecisionRefreshHandoff';
import {
  creativeLocaleHeadline,
  findCreativeLocaleKey,
  getCreativeLocaleStatus,
  getExactCreativeLocaleCopy,
  isSegmentlyOwnedCreativeItem,
  resolveCreativeLocaleProjection,
} from '../services/creativeLocaleProjection';

type WorkspaceTab = 'campaigns' | 'platforms' | 'briefs' | 'creative-sets' | 'launch' | 'history';
type BriefDetailTab = 'overview' | 'research' | 'angles' | 'design' | 'creatives';
type PlatformDetailTab = 'config' | 'design' | 'templates';
type DesignDetailTab = 'overview' | 'style' | 'creative-types';
type PresetDetailTab = 'overview' | 'settings' | 'templates' | 'rules';

const WORKSPACE_TABS: readonly WorkspaceTab[] = ['campaigns', 'platforms', 'briefs', 'creative-sets', 'launch', 'history'];
const MVP_WORKSPACE_TABS: readonly WorkspaceTab[] = ['platforms', 'briefs'];
const AD_CREATIVE_TAB_CONFIG: ReadonlyArray<{
  readonly id: WorkspaceTab;
  readonly label: string;
  readonly icon: React.ElementType;
}> = [
  { id: 'platforms', label: 'Platforms', icon: PublicIcon },
  { id: 'briefs', label: 'Briefs / Topics', icon: InsightsIcon },
] as const;
const BRIEF_DETAIL_TABS: readonly BriefDetailTab[] = ['overview', 'research', 'angles', 'design', 'creatives'];
const PLATFORM_DETAIL_TABS: readonly PlatformDetailTab[] = ['config', 'design', 'templates'];
const DESIGN_DETAIL_TABS: readonly DesignDetailTab[] = ['overview', 'style', 'creative-types'];
const PRESET_DETAIL_TABS: readonly PresetDetailTab[] = ['overview', 'settings', 'templates', 'rules'];

function readWorkspaceTab(value: string | null): WorkspaceTab | null {
  return value && (WORKSPACE_TABS as readonly string[]).includes(value) ? (value as WorkspaceTab) : null;
}

function readMvpWorkspaceTab(value: string | null): WorkspaceTab | null {
  return value && (MVP_WORKSPACE_TABS as readonly string[]).includes(value) ? (value as WorkspaceTab) : null;
}

function readBriefDetailTab(value: string | null): BriefDetailTab | null {
  return value && (BRIEF_DETAIL_TABS as readonly string[]).includes(value) ? (value as BriefDetailTab) : null;
}

function readBriefDetailTabFromSearchParams(params: URLSearchParams): BriefDetailTab | null {
  // Match Content Plan topic details: `tab` is the canonical detail-tab
  // query key. Keep `briefTab` readable for older URLs created before this
  // screen adopted the topic-detail pattern.
  return readBriefDetailTab(params.get('tab')) ?? readBriefDetailTab(params.get('briefTab'));
}

function readPlatformDetailTab(value: string | null): PlatformDetailTab | null {
  if (value === 'designs') return 'design';
  return value && (PLATFORM_DETAIL_TABS as readonly string[]).includes(value) ? (value as PlatformDetailTab) : null;
}

function readDesignDetailTab(value: string | null): DesignDetailTab | null {
  return value && (DESIGN_DETAIL_TABS as readonly string[]).includes(value) ? (value as DesignDetailTab) : null;
}

function readPresetDetailTab(value: string | null): PresetDetailTab | null {
  return value && (PRESET_DETAIL_TABS as readonly string[]).includes(value) ? (value as PresetDetailTab) : null;
}

interface CampaignForm {
  title: string;
  objectiveId: string;
  primaryKpi: string;
  networkId: string;
  publisherPlatforms: string[];
  placementFamilies: string[];
  destinationUrl: string;
}

interface BriefForm {
  title: string;
  audienceName: string;
  taskType: string;
  painOrNeed: string;
  offerPromise: string;
  cta: string;
  proofSummary: string;
  topicInstruction: string;
  researchInstruction: string;
  angleInstruction: string;
}

interface DesignCollectionForm {
  id?: string;
  name: string;
  description: string;
  isCurrent: boolean;
  palette: string;
  typography: string;
  composition: string;
  textPolicy: string;
  referencePolicy: string;
  presets: DesignPresetForm[];
}

interface DesignGenerationForm {
  name: string;
  description: string;
  sourceBrief: string;
  referenceNotes: string;
  setCurrent: boolean;
}

interface DesignPresetForm {
  id: string;
  creativeType: AdCreativePresetType;
  name: string;
  description: string;
  enabled: boolean;
  templateIds: string[];
  placementFamilies?: string[];
  placementIds?: string[];
  aspectRatios?: string[];
  slotLimitPerCreative: number;
  sourceProfileKey: string;
  slotPolicies?: Record<string, unknown>;
  styleAnchor?: Record<string, unknown>;
  brandStyleKit?: Record<string, unknown>;
  referenceStrategy?: Record<string, unknown>;
  generationDefaults?: Record<string, unknown>;
  previewSpec?: Record<string, unknown>;
}

type AdCreativePresetType = AdCreativeDesignPreset['creativeType'];

interface SourceOption {
  id: string;
  label: string;
  type: 'audience' | 'task' | 'insight' | 'proof';
  summary: string;
}

const DEFAULT_NETWORK_ID = 'meta';
const AD_CREATIVE_TASK_TYPES = [
  'ad-creative-brief-topic-generation',
  'ad-creative-research',
  'ad-creative-angle-generation',
  'ad-creative-design-collection-generation',
  'ad-creative-generation',
  'ad-creative-launch-plan-generation',
  'ad-creative-export',
  'ad-creative-metric-import',
  'ad-creative-optimization-analysis',
];

const DEFAULT_CAMPAIGN_FORM: CampaignForm = {
  title: 'Meta lead generation sprint',
  objectiveId: 'leads',
  primaryKpi: 'cpl',
  networkId: DEFAULT_NETWORK_ID,
  publisherPlatforms: ['facebook', 'instagram'],
  placementFamilies: ['feed', 'story', 'reel', 'carousel'],
  destinationUrl: 'https://example.com/demo',
};

const DEFAULT_BRIEF_FORM: BriefForm = {
  title: '',
  audienceName: '',
  taskType: '',
  painOrNeed: '',
  offerPromise: '',
  cta: '',
  proofSummary: '',
  topicInstruction: 'Generate campaign brief topics that differ by audience task, proof strategy, placement behavior, and buying move.',
  researchInstruction: 'Find objections, buying triggers, proof gaps, and placement implications for paid social ads.',
  angleInstruction: 'Generate differentiated angles by audience task, social proof, and placement family.',
};

const DEFAULT_DESIGN_GENERATION_FORM: DesignGenerationForm = {
  name: '',
  description: '',
  sourceBrief: 'Create a source-backed paid social design system that can adapt to feed, story, reel, carousel, and catalog placements without inventing proof.',
  referenceNotes: [
    'Proof card: dark mobile-safe background, short claim, visible source/proof frame, clear CTA zone.',
    'Carousel sequence: cover card, proof card, objection card, next-step CTA card.',
    'Vertical placement: single hook line, safe top/bottom zones, product/proof insert, minimal body copy.',
  ].join('\n'),
  setCurrent: true,
};

const FALLBACK_SOURCE_OPTIONS: SourceOption[] = [
  {
    id: 'aud_growth_ops',
    type: 'audience',
    label: 'Growth operators',
    summary: 'Teams responsible for turning product and audience research into paid experiments.',
  },
  {
    id: 'task_launch_variants',
    type: 'task',
    label: 'Launch variants',
    summary: 'Need to create placement-specific variants without losing strategy or proof fidelity.',
  },
  {
    id: 'insight_generic_ads_fail',
    type: 'insight',
    label: 'Generic ads fail',
    summary: 'The strongest differentiator is specialization by audience, task, proof, and platform rules.',
  },
  {
    id: 'proof_traceable_claims',
    type: 'proof',
    label: 'Traceable claims',
    summary: 'Only source-backed social proof and selected evidence should influence ad claims.',
  },
];

const SOURCE_PICKERS: PickerConfig[] = [
  { type: 'audience', label: 'Audiences', enabled: true },
  { type: 'task', label: 'Tasks', enabled: true },
  { type: 'insights', label: 'Insights', enabled: true },
  { type: 'socialproof', label: 'Social Proof', enabled: true },
];

export function AdCreativeLabPage(): React.ReactElement {
  const { projectId } = useParams();
  return (
    <ProjectLocalesProvider projectId={projectId}>
      <AdCreativeLabPageContent />
    </ProjectLocalesProvider>
  );
}

function AdCreativeLabPageContent(): React.ReactElement {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = readMvpWorkspaceTab(searchParams.get('adTab')) ?? 'briefs';
  const initialBriefTab = readBriefDetailTabFromSearchParams(searchParams) ?? 'overview';
  const client = useMemo(() => new AdCreativeApiClient(), []);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(initialTab);
  const [networks, setNetworks] = useState<AdNetwork[]>([]);
  const [selectedNetworkId, setSelectedNetworkId] = useState(DEFAULT_NETWORK_ID);
  const [templates, setTemplates] = useState<AdCreativeTemplate[]>([]);
  const [designCollections, setDesignCollections] = useState<AdCreativeDesignCollection[]>([]);
  const [selectedDesignCollectionId, setSelectedDesignCollectionId] = useState('');
  const [selectedPresetIds, setSelectedPresetIds] = useState<string[]>([]);
  const [designForm, setDesignForm] = useState<DesignCollectionForm | null>(null);
  const [designGenerationOpen, setDesignGenerationOpen] = useState(false);
  const [designGenerationForm, setDesignGenerationForm] = useState<DesignGenerationForm>(DEFAULT_DESIGN_GENERATION_FORM);
  const [designGenerationModel, setDesignGenerationModel] = useState(projectDefaultModel.value);
  const [campaigns, setCampaigns] = useState<AdCreativeCampaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [briefs, setBriefs] = useState<AdCreativeBrief[]>([]);
  const [selectedBriefId, setSelectedBriefId] = useState('');
  const [briefDetailTab, setBriefDetailTab] = useState<BriefDetailTab>(initialBriefTab);
  const [briefTopicGenerationOpen, setBriefTopicGenerationOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState<CampaignForm>(DEFAULT_CAMPAIGN_FORM);
  const [briefForm, setBriefForm] = useState<BriefForm>(DEFAULT_BRIEF_FORM);
  const [briefGenerationForm, setBriefGenerationForm] = useState<BriefForm>(DEFAULT_BRIEF_FORM);
  const [briefGenerationSourcePaths, setBriefGenerationSourcePaths] = useState<string[]>([]);
  const [briefGenerationSourceHydratedData, setBriefGenerationSourceHydratedData] = useState<HydratedData | null>(null);
  const [briefGenerationFallbackSourceIds, setBriefGenerationFallbackSourceIds] = useState<string[]>([]);
  const [briefGenerationModel, setBriefGenerationModel] = useState(projectDefaultModel.value);
  const [sourcePaths, setSourcePaths] = useState<string[]>([]);
  const [sourceHydratedData, setSourceHydratedData] = useState<HydratedData | null>(null);
  const [fallbackSourceIds, setFallbackSourceIds] = useState<string[]>([]);
  const [briefGenerationHistoryOpen, setBriefGenerationHistoryOpen] = useState(false);
  const [reviewBriefGenerationRun, setReviewBriefGenerationRun] = useState<GenerationRunSummary | null>(null);
  const [briefApplyToast, setBriefApplyToast] = useState<string | null>(null);
  const [generateAssets, setGenerateAssets] = useState(false);
  const [creativeImageModel, setCreativeImageModel] = useState('gemini-3.1-flash-image');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<AdCreativePlan | null>(null);
  const [researchRun, setResearchRun] = useState<AdCreativeResearchRun | null>(null);
  const [angles, setAngles] = useState<Array<Record<string, unknown>>>([]);
  const [items, setItems] = useState<AdCreativeItem[]>([]);
  const [lastRun, setLastRun] = useState<AdCreativeGenerationRun | null>(null);
  const [launchPlan, setLaunchPlan] = useState<Record<string, unknown> | null>(null);
  const [launchManifest, setLaunchManifest] = useState<Record<string, unknown> | null>(null);
  const [learningSnapshot, setLearningSnapshot] = useState<Record<string, unknown> | null>(null);
  const [improvementRun, setImprovementRun] = useState<Record<string, unknown> | null>(null);
  const [selectedItem, setSelectedItem] = useState<AdCreativeItem | null>(null);
  const briefGenerationModelTouchedRef = useRef(false);
  const designGenerationModelTouchedRef = useRef(false);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshQueuedRef = useRef(false);
  const refreshWorkspaceRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const upsertBackendTask = useTaskStore((state) => state.upsertTask);
  // Boolean selector: the page re-renders only when the running-flag FLIPS,
  // never on task heartbeats (the old whole-map subscription re-rendered the
  // 5k-line page on every backend_tasks write — D11).
  const hasRunningAdCreativeTask = useTaskStore((state) =>
    Object.values(state.tasks).some((task) =>
      Boolean(task.taskType && AD_CREATIVE_TASK_TYPES.includes(task.taskType))
      && ACTIVE_STATUSES.includes(task.status)));

  useEffect(() => {
    if (!briefGenerationModelTouchedRef.current) {
      setBriefGenerationModel(projectDefaultModel.value);
    }
  }, [projectDefaultModel.value]);

  useEffect(() => {
    if (!designGenerationModelTouchedRef.current) {
      setDesignGenerationModel(projectDefaultModel.value);
    }
  }, [projectDefaultModel.value]);
  const { runs: briefGenerationRuns, loading: briefGenerationRunsLoading } = useGenerationRuns(projectId || null, 'adBriefTopic');

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign.id === selectedCampaignId) ?? null,
    [campaigns, selectedCampaignId],
  );
  const selectedDesignCollection = useMemo(
    () => designCollections.find((collection) => collection.id === selectedDesignCollectionId) ?? null,
    [designCollections, selectedDesignCollectionId],
  );
  const selectedPresets = useMemo(
    () => (selectedDesignCollection?.presets ?? []).filter((preset) => selectedPresetIds.includes(preset.id)),
    [selectedDesignCollection, selectedPresetIds],
  );
  const selectedTemplateIds = useMemo(
    () => uniqueStrings(selectedPresets.flatMap((preset) => preset.templateIds ?? [])),
    [selectedPresets],
  );
  const designTemplates = useMemo(
    () => templates.filter((template) => selectedTemplateIds.includes(template.id)),
    [selectedTemplateIds, templates],
  );
  const activeBrief = useMemo(
    () => briefs.find((brief) => brief.id === selectedBriefId) ?? null,
    [briefs, selectedBriefId],
  );
  const platformOptions = useMemo(
    () => (networks.length > 0
      ? networks
      : [{ id: DEFAULT_NETWORK_ID, displayName: 'Meta', isActive: true } as AdNetwork]),
    [networks],
  );
  const urlBatchId = searchParams.get('batch');
  const urlBatchStep = readBatchStep(searchParams.get('step'));
  const urlBatchItemId = searchParams.get('item');
  const urlView = searchParams.get('view');
  const showCreativeDecisionRefreshDraft = searchParams.get('mode') === 'refresh-experiment-draft';
  const creativeDecisionRefreshParse = useMemo(
    () => parseCreativeDecisionRefreshHandoff(searchParams),
    [searchParams],
  );
  const localizedVariationHandoff = useMemo(
    () => parseLocalizedVariationHandoff(searchParams, projectId),
    [projectId, searchParams],
  );
  // FR5: `?batch=&item=&view=storyboard[&scene=N]` → fullscreen storyboard.
  const urlSceneParam = searchParams.get('scene');
  const urlSceneIndex = urlSceneParam !== null && Number.isInteger(Number(urlSceneParam)) ? Number(urlSceneParam) : null;
  const showStoryboard = Boolean(urlBatchId && urlBatchItemId && urlView === 'storyboard');
  // Default-first IA (U2): the prompt-bar home is the entry; legacy tabs stay
  // reachable through explicit ?adTab= links until their U4 replacements.
  const showCreateHome = !urlBatchId && (urlView === 'home' || (!urlView && !searchParams.get('adTab') && !searchParams.get('brief')));
  const urlBriefId = searchParams.get('brief');
  const urlPlatformId = searchParams.get('adPlatform');
  const urlDesignCollectionId = searchParams.get('designCollection');
  const urlCreativePresetId = searchParams.get('creativePreset');
  const platformDetailTab = readPlatformDetailTab(searchParams.get('platformTab')) ?? 'design';
  const designDetailTab = readDesignDetailTab(searchParams.get('designTab')) ?? (
    urlDesignCollectionId === '__draft__' ? 'style' : 'overview'
  );
  const presetDetailTab = readPresetDetailTab(searchParams.get('presetTab')) ?? 'overview';
  const platformDetailNetwork = useMemo(
    () => platformOptions.find((network) => network.id === (urlPlatformId || selectedNetworkId)) ?? platformOptions[0] ?? null,
    [platformOptions, selectedNetworkId, urlPlatformId],
  );
  const designDetailCollection = useMemo(
    () => {
      if (urlDesignCollectionId === '__draft__' && designForm) return designFormPreview(designForm);
      if (!urlDesignCollectionId) return null;
      return designCollections.find((collection) => collection.id === urlDesignCollectionId) ?? null;
    },
    [designCollections, designForm, urlDesignCollectionId],
  );
  const creativePresetDetail = useMemo(
    () => {
      if (!urlCreativePresetId) return null;
      const availablePresets = [
        ...(designForm?.presets ?? []),
        ...(designDetailCollection?.presets ?? []),
      ];
      return availablePresets.find((preset) => preset.id === urlCreativePresetId)
        ?? availablePresets.find((preset) => urlCreativePresetId.endsWith(`_${preset.creativeType}`))
        ?? null;
    },
    [designDetailCollection, designForm, urlCreativePresetId],
  );
  const briefDetailTopic = useMemo(
    () => {
      if (!urlBriefId) return null;
      return briefs.find((brief) => brief.id === urlBriefId) ?? ({
        id: urlBriefId,
        title: 'Brief / topic',
        projectId,
        networkId: selectedNetworkId,
        campaignId: selectedCampaignId,
        status: 'draft',
      } as AdCreativeBrief);
    },
    [briefs, projectId, selectedCampaignId, selectedNetworkId, urlBriefId],
  );
  const selectedFallbackSources = useMemo(
    () => FALLBACK_SOURCE_OPTIONS.filter((option) => fallbackSourceIds.includes(option.id)),
    [fallbackSourceIds],
  );
  const selectedBriefGenerationFallbackSources = useMemo(
    () => FALLBACK_SOURCE_OPTIONS.filter((option) => briefGenerationFallbackSourceIds.includes(option.id)),
    [briefGenerationFallbackSourceIds],
  );
  const campaignItems = useMemo(
    () => selectedCampaign ? items.filter((item) => item.campaignId === selectedCampaign.id) : items,
    [items, selectedCampaign],
  );
  const activeBriefItems = useMemo(
    () => {
      const briefId = briefDetailTopic?.id || activeBrief?.id;
      return briefId ? items.filter((item) => item.briefId === briefId) : [];
    },
    [activeBrief, briefDetailTopic, items],
  );
  const activeRunForLaunch = useMemo(
    () => lastRun ?? (
      campaignItems[0]?.generationRunId
        ? ({
            id: campaignItems[0].generationRunId,
            status: 'completed',
            projectId,
            networkId: campaignItems[0].networkId,
            generatedItemIds: campaignItems
              .filter((item) => item.generationRunId === campaignItems[0].generationRunId)
              .map((item) => item.id),
          } as unknown as AdCreativeGenerationRun)
        : null
    ),
    [campaignItems, lastRun, projectId],
  );

  useEffect(() => {
    if (urlView === 'import') return;
    void refreshWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedNetworkId, urlView]);

  useEffect(() => {
    const nextTab = readMvpWorkspaceTab(searchParams.get('adTab'));
    const nextBriefId = searchParams.get('brief');
    const nextBriefTab = readBriefDetailTabFromSearchParams(searchParams);
    const nextPlatformId = searchParams.get('adPlatform');
    const nextDesignCollectionId = searchParams.get('designCollection');
    if (nextTab && nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
    if (nextBriefId) {
      if (activeTab !== 'briefs') setActiveTab('briefs');
      if (nextBriefId !== selectedBriefId) setSelectedBriefId(nextBriefId);
    }
    if (nextPlatformId) {
      if (activeTab !== 'platforms') setActiveTab('platforms');
      if (nextPlatformId !== selectedNetworkId) setSelectedNetworkId(nextPlatformId);
    }
    if (nextDesignCollectionId && nextDesignCollectionId !== '__draft__' && nextDesignCollectionId !== selectedDesignCollectionId) {
      setSelectedDesignCollectionId(nextDesignCollectionId);
    }
    if (nextBriefTab && nextBriefTab !== briefDetailTab) {
      setBriefDetailTab(nextBriefTab);
    }
  }, [activeTab, briefDetailTab, searchParams, selectedBriefId, selectedDesignCollectionId, selectedNetworkId]);

  useEffect(() => {
    setCampaignForm((current) => ({ ...current, networkId: selectedNetworkId }));
  }, [selectedNetworkId]);

  useEffect(() => {
    const presetIds = selectedDesignCollection?.presets?.map((preset) => preset.id) ?? [];
    if (presetIds.length === 0) {
      setSelectedPresetIds([]);
      return;
    }
    setSelectedPresetIds((current) => {
      const kept = current.filter((id) => presetIds.includes(id));
      return kept.length > 0 ? kept : presetIds;
    });
  }, [selectedDesignCollection]);

  useEffect(() => {
    if (!selectedDesignCollection) {
      setDesignForm(null);
      return;
    }
    setDesignForm(collectionToDesignForm(selectedDesignCollection));
  }, [selectedDesignCollection]);

  // Task lifecycle (FR0-I1): ONE transient store subscription replaces the old
  // whole-map subscription + per-action setTimeout refresh ladders. Fires once
  // per status transition; heartbeats and historical tasks never reach it.
  useTaskTransition({
    filter: (task) => Boolean(task.taskType && AD_CREATIVE_TASK_TYPES.includes(task.taskType)),
    onTransition: (task) => {
      if (!isTerminalTaskStatus(task.status)) return;
      const taskType = String(task.taskType ?? '');
      // The old ladders also re-fetched after failures (they armed at action
      // start), so any terminal outcome refreshes; artifact hydration below
      // stays completed-only like before.
      if (
        taskType === 'ad-creative-brief-topic-generation'
        || taskType === 'ad-creative-angle-generation'
        || taskType === 'ad-creative-design-collection-generation'
        || taskType === 'ad-creative-generation'
      ) {
        void refreshWorkspaceRef.current();
      }
      if (task.status !== 'completed') return;
      const artifacts = getTaskArtifacts(task);
      if (taskType === 'ad-creative-research') {
        const researchRunId = artifactString(artifacts, 'researchRunId');
        if (researchRunId) {
          setResearchRun((current) => current?.id === researchRunId ? current : {
            id: researchRunId,
            status: 'completed',
          });
        }
      }
      if (taskType === 'ad-creative-launch-plan-generation') {
        const launchPlanId = artifactString(artifacts, 'launchPlanId');
        if (launchPlanId) {
          setLaunchPlan((current) => String(current?.id ?? '') === launchPlanId ? current : {
            id: launchPlanId,
            status: 'draft',
            createdFromTaskId: task.id,
          });
        }
      }
      if (taskType === 'ad-creative-export') {
        const manifestId = artifactString(artifacts, 'manifestId');
        if (manifestId) {
          setLaunchManifest((current) => String(current?.id ?? '') === manifestId ? current : {
            id: manifestId,
            status: 'ready',
            createdFromTaskId: task.id,
          });
        }
      }
      if (taskType === 'ad-creative-metric-import') {
        const learningSnapshotId = artifactString(artifacts, 'learningSnapshotId');
        if (learningSnapshotId) {
          setLearningSnapshot((current) => String(current?.id ?? '') === learningSnapshotId ? current : {
            id: learningSnapshotId,
            status: 'ready',
            createdFromTaskId: task.id,
          });
        }
      }
      if (taskType === 'ad-creative-optimization-analysis') {
        const improvementRunId = artifactString(artifacts, 'improvementRunId');
        if (improvementRunId) {
          setImprovementRun((current) => String(current?.id ?? '') === improvementRunId ? current : {
            id: improvementRunId,
            status: 'proposal_ready',
            createdFromTaskId: task.id,
          });
        }
      }
    },
  });

  // Safety net (FR0-PRE-003): while an ad-creative task runs, ONE slow 60s
  // interval keeps the workspace fresh in case a transition is missed. Torn
  // down when the last task settles or the page unmounts.
  useEffect(() => {
    if (!hasRunningAdCreativeTask) return undefined;
    const interval = window.setInterval(() => {
      void refreshWorkspaceRef.current();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [hasRunningAdCreativeTask]);

  // Stable batch-workspace callbacks (FR0-I2): the memoized workspace must not
  // re-render just because this 5k-line page re-rendered and recreated arrows.
  const setBatchUrlStateRef = useRef<(patch: { batchId?: string | null; step?: BatchStepKey | null; itemId?: string | null; storyboard?: boolean; scene?: number | null }) => void>(() => undefined);
  const handleBatchStepChange = useCallback((step: BatchStepKey) => setBatchUrlStateRef.current({ step }), []);
  const handleBatchSelectItem = useCallback((itemId: string | null) => setBatchUrlStateRef.current({ itemId }), []);
  const handleBatchBack = useCallback(() => setBatchUrlStateRef.current({ batchId: null }), []);
  // FR5: drawer «Open storyboard ↗» / «Make video» land into the workspace.
  const handleBatchOpenStoryboard = useCallback((itemId: string) => setBatchUrlStateRef.current({ itemId, storyboard: true }), []);

  async function refreshWorkspace(): Promise<void> {
    // The provider-source import owns its own narrowly scoped reads. Loading
    // the full authored workspace here adds unrelated item/catalog requests,
    // can surface irrelevant schema errors, and slows the reconnect path.
    if (!projectId || urlView === 'import') return;
    setLoading(true);
    setError(null);
    setItemsError(null);
    try {
      const [
        networksResponse,
        catalogResponse,
        collectionsResponse,
        campaignResponse,
        briefResponse,
        itemsResponse,
      ] = await Promise.all([
        client.listNetworks(),
        client.getCatalog(selectedNetworkId),
        client.listDesignCollections(projectId, selectedNetworkId),
        client.listCampaigns(projectId, { limit: 50 }),
        client.listBriefs(projectId, { networkId: selectedNetworkId, limit: 100 }),
        client.listItems(projectId, { networkId: selectedNetworkId, limit: 100 }),
      ]);

      if (!catalogResponse.success || !catalogResponse.data) {
        setError(catalogResponse.error || 'Failed to load ad creative template catalog');
      } else {
        setTemplates(catalogResponse.data.templates);
      }

      if (networksResponse.success && networksResponse.data?.networks?.length) {
        setNetworks(networksResponse.data.networks);
      } else if (catalogResponse.data?.network) {
        setNetworks([catalogResponse.data.network]);
      }

      if (collectionsResponse.success && collectionsResponse.data) {
        const collections = collectionsResponse.data.collections;
        setDesignCollections(collections);
        setSelectedDesignCollectionId((current) => {
          if (collections.some((collection) => collection.id === current)) return current;
          return collections.find((collection) => collection.isCurrent || collection.isDefault)?.id ?? collections[0]?.id ?? '';
        });
      }

      if (campaignResponse.success && campaignResponse.data) {
        const nextCampaigns = campaignResponse.data.campaigns;
        setCampaigns((current) => mergeCampaigns(nextCampaigns, current));
        setSelectedCampaignId((current) => (nextCampaigns.some((campaign) => campaign.id === current) ? current : ''));
      }

      if (briefResponse.success && briefResponse.data) {
        const nextBriefs = briefResponse.data.briefs.filter((brief) => brief.status !== 'archived');
        setBriefs((current) => mergeBriefs(nextBriefs, current));
        setSelectedBriefId((current) => {
          if (nextBriefs.some((brief) => brief.id === current)) return current;
          return '';
        });
      }

      if (itemsResponse.success && itemsResponse.data) {
        setItems(itemsResponse.data.items.filter(isSegmentlyOwnedCreativeItem));
      } else {
        setItemsError(creativeLibraryLoadMessage(itemsResponse.error));
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLoading(false);
    }
  }

  // Coalesced refresh (FR0-I1): one in-flight refresh at a time; transitions
  // arriving mid-refresh queue exactly one trailing run.
  function refreshWorkspaceCoalesced(): Promise<void> {
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return refreshInFlightRef.current;
    }
    const run = (async () => {
      try {
        await refreshWorkspace();
      } finally {
        refreshInFlightRef.current = null;
        if (refreshQueuedRef.current) {
          refreshQueuedRef.current = false;
          void refreshWorkspaceCoalesced();
        }
      }
    })();
    refreshInFlightRef.current = run;
    return run;
  }
  refreshWorkspaceRef.current = refreshWorkspaceCoalesced;

  async function createCampaign(): Promise<AdCreativeCampaign | null> {
    if (!projectId) return null;
    let created: AdCreativeCampaign | null = null;
    await runAction('Campaign created', async () => {
      const response = await client.createCampaign({
        projectId,
        title: campaignForm.title,
        objectiveId: campaignForm.objectiveId,
        primaryKpi: campaignForm.primaryKpi,
        networkId: campaignForm.networkId || selectedNetworkId,
        selectedPublisherPlatforms: campaignForm.publisherPlatforms,
        selectedPlacementFamilies: campaignForm.placementFamilies,
        destinationRefs: campaignForm.destinationUrl
          ? [{ type: 'url', url: campaignForm.destinationUrl, label: 'Landing page' }]
          : [],
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to create campaign');
      created = response.data.campaign;
      setCampaigns((current) => mergeCampaigns([response.data!.campaign], current));
      setSelectedCampaignId(response.data.campaign.id);
      selectWorkspaceTab('briefs');
    });
    return created;
  }

  function buildBriefRequest(campaign?: AdCreativeCampaign | null): CreateAdCreativeBriefRequest {
    const sourceRefs = buildSourceRefs(sourcePaths, sourceHydratedData, selectedFallbackSources);
    const networkId = campaign?.networkId || selectedNetworkId || DEFAULT_NETWORK_ID;
    return {
      projectId,
      campaignId: campaign?.id,
      title: briefForm.title,
      networkId,
      objective: campaign?.objectiveId || campaignForm.objectiveId,
      kpi: campaign?.primaryKpi || campaignForm.primaryKpi,
      audience: {
        name: briefForm.audienceName,
        sourcePaths,
        fallbackSources: selectedFallbackSources,
        hydrated: sourceHydratedData,
      },
      task: {
        type: briefForm.taskType,
        painOrNeed: briefForm.painOrNeed,
        sourcePaths: sourcePaths.filter((path) => path.startsWith('@task[')),
        hydrated: sourceHydratedData?.tasks ?? [],
      },
      destination: campaignForm.destinationUrl
        ? { type: 'url', url: campaignForm.destinationUrl, label: 'Landing page' }
        : undefined,
      placementFamilies: campaign?.selectedPlacementFamilies?.length ? campaign.selectedPlacementFamilies : campaignForm.placementFamilies,
      productContext: {
        campaignId: campaign?.id,
        campaignTitle: campaign?.title,
        offerPromise: briefForm.offerPromise,
        cta: briefForm.cta,
        proofSummary: briefForm.proofSummary,
      },
      sourceRefs,
      selectedTemplateIds,
      designCollectionId: selectedDesignCollection?.id,
      selectedDesignPresetIds: selectedPresetIds,
      variantBudget: Math.max(1, selectedTemplateIds.length),
    };
  }

  async function createManualBrief(): Promise<AdCreativeBrief | null> {
    if (!projectId) return null;
    let createdBrief: AdCreativeBrief | null = null;
    await runAction('Brief topic added', async () => {
      const response = await client.createBrief(buildBriefRequest(null));
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to create campaign brief');
      createdBrief = response.data.brief;
      setBriefs((current) => mergeBriefs([response.data!.brief], current));
      setSelectedBriefId(response.data.brief.id);
      openBriefTopic(response.data.brief.id);
    });
    return createdBrief;
  }

  async function archiveBriefTopics(briefIds: string[]): Promise<void> {
    if (!projectId || briefIds.length === 0) return;
    await runAction(briefIds.length === 1 ? 'Brief topic archived' : `${briefIds.length} brief topics archived`, async () => {
      await Promise.all(briefIds.map(async (briefId) => {
        const response = await client.archiveBrief(projectId, briefId);
        if (!response.success) throw new Error(response.error || `Failed to archive brief ${briefId}`);
      }));
      setBriefs((current) => current.filter((brief) => !briefIds.includes(brief.id)));
      if (briefIds.includes(selectedBriefId)) {
        setSelectedBriefId('');
        setUrlState({ adTab: 'briefs', briefId: null, briefTab: null });
      }
      await refreshWorkspace();
    });
  }

  async function generateBriefTopics(): Promise<void> {
    if (!projectId) return;
    const generationContext = resolveBriefGenerationContext({
      form: briefGenerationForm,
      sourcePaths: briefGenerationSourcePaths,
      hydratedData: briefGenerationSourceHydratedData,
      fallbackSources: selectedBriefGenerationFallbackSources,
    });
    if (!generationContext.canGenerate) {
      setError(generationContext.missingMessage);
      return;
    }
    await runAction('Brief topic generation task started', async () => {
      const sourceRefs = buildSourceRefs(
        briefGenerationSourcePaths,
        briefGenerationSourceHydratedData,
        selectedBriefGenerationFallbackSources,
      );
      const networkId = selectedNetworkId || DEFAULT_NETWORK_ID;
      const response = await client.generateBriefTopics({
        projectId,
        networkId,
        model: briefGenerationModel,
        objective: campaignForm.objectiveId,
        kpi: campaignForm.primaryKpi,
        count: 5,
        audience: {
          name: generationContext.audienceName,
          sourcePaths: briefGenerationSourcePaths,
          fallbackSources: selectedBriefGenerationFallbackSources,
          hydrated: briefGenerationSourceHydratedData,
        },
        task: {
          type: generationContext.taskType,
          painOrNeed: briefGenerationForm.painOrNeed || generationContext.taskSummary,
          sourcePaths: briefGenerationSourcePaths.filter((path) => path.startsWith('@task[')),
          hydrated: briefGenerationSourceHydratedData?.tasks ?? [],
        },
        destination: campaignForm.destinationUrl
          ? { type: 'url', url: campaignForm.destinationUrl, label: 'Landing page' }
          : undefined,
        placementFamilies: campaignForm.placementFamilies,
        productContext: {
          offerPromise: briefGenerationForm.offerPromise,
          cta: briefGenerationForm.cta,
          proofSummary: briefGenerationForm.proofSummary,
        },
        sourceRefs,
        selectedTemplateIds,
        designCollectionId: selectedDesignCollection?.id,
        selectedDesignPresetIds: selectedPresetIds,
        userInstruction: briefGenerationForm.topicInstruction,
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to generate brief topics');
      pushTask(response.data.task);      setBriefTopicGenerationOpen(false);
      setBriefGenerationHistoryOpen(true);
    });
  }

  async function runResearchForSelectedBrief(
    briefOverride?: AdCreativeBrief | null,
    campaignOverride?: AdCreativeCampaign | null,
  ): Promise<void> {
    const campaign = campaignOverride ?? selectedCampaign;
    const brief = briefOverride ?? activeBrief;
    if (!projectId || !brief) return;
    await runAction('Research task started for selected brief', async () => {
      const response = await client.createResearchRun(
        projectId,
        campaign?.id,
        brief.id,
        { userInstruction: briefForm.researchInstruction },
      );
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to run research');
      if (response.data.researchRun) setResearchRun(response.data.researchRun);
      pushTask(response.data.task);    });
  }

  async function generateAnglesForSelectedBrief(
    briefOverride?: AdCreativeBrief | null,
    campaignOverride?: AdCreativeCampaign | null,
  ): Promise<void> {
    const campaign = campaignOverride ?? selectedCampaign;
    const brief = briefOverride ?? activeBrief;
    if (!projectId || !brief) return;
    await runAction('Angle generation task started', async () => {
      const response = await client.generateAngles(
        projectId,
        campaign?.id,
        brief.id,
        { count: 4, mode: 'replace', userInstruction: briefForm.angleInstruction },
      );
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to generate angles');
      if (response.data.angles) setAngles(response.data.angles);
      pushTask(response.data.task);    });
  }

  async function saveDesignCollectionForm(): Promise<void> {
    if (!projectId || !designForm) return;
    await runAction('Design collection saved', async () => {
      const response = await client.saveDesignCollection(formToSaveDesignRequest({
        projectId,
        networkId: selectedNetworkId,
        form: designForm,
      }));
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to save design collection');
      const collection = response.data.collection;
      setDesignCollections((current) => mergeDesignCollections([collection], current));
      setSelectedDesignCollectionId(collection.id);
      const activePresetIds = (collection.presets ?? [])
        .filter((preset) => preset.status !== 'archived')
        .map((preset) => preset.id);
      setSelectedPresetIds(activePresetIds);
      setDesignForm(collectionToDesignForm(collection));
      if (urlDesignCollectionId === '__draft__') {
        setUrlState({
          adTab: 'platforms',
          platformId: collection.networkId || selectedNetworkId,
          platformTab: 'design',
          designCollectionId: collection.id,
          designTab: readDesignDetailTab(searchParams.get('designTab')) ?? 'style',
        });
      }
    });
  }

  async function generateDesignCollectionFromReferences(): Promise<void> {
    if (!projectId) return;
    const referenceAssets = parseDesignReferenceNotes(designGenerationForm.referenceNotes);
    await runAction('Design collection generation task started', async () => {
      const response = await client.generateDesignCollection({
        projectId,
        networkId: selectedNetworkId,
        model: designGenerationModel,
        baseCollectionId: selectedDesignCollection?.id,
        name: designGenerationForm.name || `${platformDetailNetwork?.displayName || selectedNetworkId} reference design`,
        description: designGenerationForm.description,
        sourceBrief: designGenerationForm.sourceBrief,
        referenceAssets,
        selectedCreativeTypes: selectedPresets.length > 0
          ? selectedPresets.map((preset) => preset.creativeType)
          : undefined,
        setCurrent: designGenerationForm.setCurrent,
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to generate design collection');
      pushTask(response.data.task);
      setDesignGenerationOpen(false);    });
  }

  function startNewDesignCollection(): void {
    const base = designForm ?? (selectedDesignCollection ? collectionToDesignForm(selectedDesignCollection) : null);
    if (!base) return;
    setDesignForm({
      ...base,
      id: undefined,
      name: `${base.name} custom`,
      isCurrent: false,
      presets: base.presets.map((preset) => ({
        ...preset,
        id: preset.id.replace(/^([^_]+_)?/, `${selectedNetworkId}_custom_`),
      })),
    });
  }

  async function createPlan(briefOverride?: AdCreativeBrief | null): Promise<AdCreativePlan> {
    const brief = briefOverride ?? activeBrief;
    if (!projectId || !brief) throw new Error('Create or select a campaign brief first');
    const planResponse = await client.createPlan(projectId, brief.id, {
      templateIds: selectedTemplateIds,
      variantBudget: selectedTemplateIds.length,
    });
    if (!planResponse.success || !planResponse.data) throw new Error(planResponse.error || 'Failed to create creative plan');
    setCurrentPlan(planResponse.data.plan);
    return planResponse.data.plan;
  }

  async function planAndGenerate(briefOverride?: AdCreativeBrief | null, campaignOverride?: AdCreativeCampaign | null): Promise<void> {
    const campaign = campaignOverride ?? selectedCampaign;
    const brief = briefOverride ?? activeBrief;
    if (!projectId || !brief) return;
    await runAction('Creative generation task started', async () => {
      const plan = currentPlan?.briefId === brief.id ? currentPlan : null;
      const templateIds = plan?.selectedTemplateIds
        ?? uniqueStrings([
          ...selectedTemplateIds,
          ...(brief.selectedTemplateIds ?? []),
        ]);
      const response = await client.generateTask({
        projectId,
        campaignId: campaign?.id,
        briefId: brief.id,
        planId: plan?.id,
        networkId: campaign?.networkId || brief.networkId || selectedNetworkId || DEFAULT_NETWORK_ID,
        templateIds,
        variantCount: plan?.rows?.length ?? templateIds.length,
        generateAssets,
        imageModel: generateAssets ? creativeImageModel : undefined,
        slotLimitPerCreative: resolveSlotLimit(selectedPresets),
        designCollectionId: selectedDesignCollection?.id ?? brief.designCollectionId,
        selectedDesignPresetIds: selectedPresetIds.length > 0 ? selectedPresetIds : brief.selectedDesignPresetIds,
        designPresetByCreativeType: buildPresetMap(selectedPresets),
        audience: {
          name: briefForm.audienceName,
          taskType: briefForm.taskType,
          painOrNeed: briefForm.painOrNeed,
          sourcePaths,
          hydrated: sourceHydratedData,
        },
        offer: {
          promise: briefForm.offerPromise,
          cta: briefForm.cta,
        },
        proof: {
          summary: briefForm.proofSummary,
          sourceRefs: buildSourceRefs(sourcePaths, sourceHydratedData, selectedFallbackSources),
          claimStrictness: 'source_backed_only',
        },
        destination: campaignForm.destinationUrl
          ? { type: 'url', url: campaignForm.destinationUrl, label: 'Landing page' }
          : undefined,
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to generate creative set');
      pushTask(response.data.task);
      if (response.data.run) setLastRun(response.data.run);
      if (response.data.items?.length) {
        setItems((current) => mergeItems((response.data!.items ?? []).filter(isSegmentlyOwnedCreativeItem), current));
      }      openBriefTopic(brief.id, 'creatives');
    });
  }

  async function createLaunchPlan(): Promise<void> {
    const runId = activeRunForLaunch?.id ?? campaignItems[0]?.generationRunId;
    if (!projectId || !selectedCampaign || !runId) return;
    await runAction('Launch plan task started', async () => {
      const response = await client.createLaunchPlan(projectId, selectedCampaign.id, runId, {
        runId,
        dailyBudget: 75,
        budgetMode: 'ad_set_budget',
        schedule: { startMode: 'manual_after_review' },
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to create launch plan');
      if (response.data.launchPlan) setLaunchPlan(response.data.launchPlan);
      pushTask(response.data.task);    });
  }

  async function exportLaunchPlan(): Promise<void> {
    if (!projectId || !launchPlan) return;
    await runAction('Launch export task started', async () => {
      const response = await client.exportLaunchPlan(projectId, String(launchPlan.id), { mode: 'manual_csv' });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to export launch plan');
      if (response.data.manifest) setLaunchManifest(response.data.manifest);
      pushTask(response.data.task);    });
  }

  async function importMetrics(): Promise<void> {
    if (!projectId || !selectedCampaign || !launchPlan) return;
    await runAction('Metric import task started', async () => {
      const response = await client.importMetrics(projectId, selectedCampaign.id, String(launchPlan.id), {
        metricSource: 'manual_csv',
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to import metrics');
      if (response.data.learningSnapshot) setLearningSnapshot(response.data.learningSnapshot);
      pushTask(response.data.task);    });
  }

  async function improveCampaign(): Promise<void> {
    if (!projectId || !selectedCampaign || !learningSnapshot) return;
    await runAction('Improvement task started', async () => {
      const response = await client.improveCampaign(projectId, selectedCampaign.id, {
        learningSnapshotId: String(learningSnapshot.id),
      });
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to create improvement proposal');
      if (response.data.improvementRun) setImprovementRun(response.data.improvementRun);
      pushTask(response.data.task);    });
  }

  async function runAction(successMessage: string, action: () => Promise<void>): Promise<void> {
    setRunning(true);
    setError(null);
    setStatus(null);
    try {
      await action();
      setRunning(false);
      setStatus(successMessage);
    } catch (nextError) {
      setRunning(false);
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    }
  }

  function pushTask(task: AdCreativeTaskDocument): void {
    // One synchronous upsert makes the new task visible before the Firestore
    // snapshot echo lands; the store subscription owns everything after that
    // (FR0-I1 — the 7-step per-task poll ladder is gone).
    upsertBackendTask(adCreativeTaskToBackendTask(task, projectId));
  }

  function toggleFallbackSource(sourceId: string): void {
    setFallbackSourceIds((current) =>
      current.includes(sourceId)
        ? current.filter((id) => id !== sourceId)
        : [...current, sourceId],
    );
  }

  function toggleBriefGenerationFallbackSource(sourceId: string): void {
    setBriefGenerationFallbackSourceIds((current) =>
      current.includes(sourceId)
        ? current.filter((id) => id !== sourceId)
        : [...current, sourceId],
    );
  }

  function openBriefTopicGenerationDialog(): void {
    setBriefGenerationForm(DEFAULT_BRIEF_FORM);
    setBriefGenerationSourcePaths([]);
    setBriefGenerationSourceHydratedData(null);
    setBriefGenerationFallbackSourceIds([]);
    setBriefTopicGenerationOpen(true);
  }

  function togglePreset(presetId: string): void {
    setSelectedPresetIds((current) =>
      current.includes(presetId)
        ? current.filter((id) => id !== presetId)
        : [...current, presetId],
    );
  }

  function setUrlState(patch: {
    adTab?: WorkspaceTab;
    briefId?: string | null;
    briefTab?: BriefDetailTab | null;
    platformId?: string | null;
    platformTab?: PlatformDetailTab | null;
    designCollectionId?: string | null;
    designTab?: DesignDetailTab | null;
    presetId?: string | null;
    presetTab?: PresetDetailTab | null;
  }): void {
    const next = new URLSearchParams(searchParams);
    if (patch.adTab) {
      next.set('adTab', patch.adTab);
      next.delete('view');
    }
    if (patch.briefId !== undefined) {
      if (patch.briefId) next.set('brief', patch.briefId);
      else next.delete('brief');
    }
    if (patch.briefTab !== undefined) {
      if (patch.briefTab) next.set('tab', patch.briefTab);
      else next.delete('tab');
      next.delete('briefTab');
    }
    if (patch.platformId !== undefined) {
      if (patch.platformId) next.set('adPlatform', patch.platformId);
      else next.delete('adPlatform');
    }
    if (patch.platformTab !== undefined) {
      if (patch.platformTab) next.set('platformTab', patch.platformTab);
      else next.delete('platformTab');
    }
    if (patch.designCollectionId !== undefined) {
      if (patch.designCollectionId) next.set('designCollection', patch.designCollectionId);
      else next.delete('designCollection');
    }
    if (patch.designTab !== undefined) {
      if (patch.designTab) next.set('designTab', patch.designTab);
      else next.delete('designTab');
    }
    if (patch.presetId !== undefined) {
      if (patch.presetId) next.set('creativePreset', patch.presetId);
      else next.delete('creativePreset');
    }
    if (patch.presetTab !== undefined) {
      if (patch.presetTab) next.set('presetTab', patch.presetTab);
      else next.delete('presetTab');
    }
    setSearchParams(next, { replace: false });
  }

  function setBatchUrlState(patch: { batchId?: string | null; step?: BatchStepKey | null; itemId?: string | null; storyboard?: boolean; scene?: number | null }): void {
    const next = new URLSearchParams(searchParams);
    if (patch.batchId !== undefined) {
      if (patch.batchId) next.set('batch', patch.batchId);
      else {
        next.delete('batch');
        next.delete('step');
        next.delete('item');
        next.delete('view');
        next.delete('scene');
      }
    }
    if (patch.step !== undefined) {
      if (patch.step) next.set('step', patch.step);
      else next.delete('step');
    }
    if (patch.itemId !== undefined) {
      if (patch.itemId) next.set('item', patch.itemId);
      else next.delete('item');
    }
    // FR5 storyboard view + fullscreen scene param.
    if (patch.storyboard !== undefined) {
      if (patch.storyboard) next.set('view', 'storyboard');
      else {
        next.delete('view');
        next.delete('scene');
      }
    }
    if (patch.scene !== undefined) {
      if (patch.scene !== null) next.set('scene', String(patch.scene));
      else next.delete('scene');
    }
    setSearchParams(next, { replace: false });
  }
  setBatchUrlStateRef.current = setBatchUrlState;

  function selectWorkspaceTab(nextTab: WorkspaceTab): void {
    setActiveTab(nextTab);
    setUrlState({
      adTab: nextTab,
      briefId: nextTab === 'briefs' ? undefined : null,
      briefTab: nextTab === 'briefs' ? undefined : null,
      platformId: nextTab === 'platforms' ? undefined : null,
      platformTab: nextTab === 'platforms' ? undefined : null,
      designCollectionId: nextTab === 'platforms' ? undefined : null,
      designTab: nextTab === 'platforms' ? undefined : null,
      presetId: nextTab === 'platforms' ? undefined : null,
      presetTab: nextTab === 'platforms' ? undefined : null,
    });
  }

  function openBriefTopic(briefId: string, tab: BriefDetailTab = 'overview'): void {
    setSelectedBriefId(briefId);
    setActiveTab('briefs');
    setBriefDetailTab(tab);
    setUrlState({ adTab: 'briefs', briefId, briefTab: tab });
  }

  function closeBriefTopic(): void {
    setSelectedBriefId('');
    setUrlState({ adTab: 'briefs', briefId: null, briefTab: null });
  }

  function changeBriefTopicTab(tab: BriefDetailTab): void {
    setBriefDetailTab(tab);
    setUrlState({ adTab: 'briefs', briefId: selectedBriefId || urlBriefId, briefTab: tab });
  }

  function openAdPlatform(networkId: string, tab: PlatformDetailTab = 'design'): void {
    setSelectedNetworkId(networkId);
    setActiveTab('platforms');
    setUrlState({ adTab: 'platforms', platformId: networkId, platformTab: tab });
  }

  function closeAdPlatform(): void {
    setUrlState({
      adTab: 'platforms',
      platformId: null,
      platformTab: null,
      designCollectionId: null,
      designTab: null,
      presetId: null,
      presetTab: null,
    });
  }

  function changeAdPlatformTab(tab: PlatformDetailTab): void {
    setUrlState({ adTab: 'platforms', platformId: urlPlatformId || selectedNetworkId, platformTab: tab });
  }

  function openDesignCollection(collectionId: string, tab: DesignDetailTab = 'overview'): void {
    if (collectionId !== '__draft__') setSelectedDesignCollectionId(collectionId);
    setUrlState({
      adTab: 'platforms',
      platformId: urlPlatformId || selectedNetworkId,
      platformTab: 'design',
      designCollectionId: collectionId,
      designTab: tab,
      presetId: null,
      presetTab: null,
    });
  }

  function closeDesignCollection(): void {
    setUrlState({
      adTab: 'platforms',
      platformId: urlPlatformId || selectedNetworkId,
      platformTab: 'design',
      designCollectionId: null,
      designTab: null,
      presetId: null,
      presetTab: null,
    });
  }

  function changeDesignCollectionTab(tab: DesignDetailTab): void {
    setUrlState({
      adTab: 'platforms',
      platformId: urlPlatformId || selectedNetworkId,
      platformTab: 'design',
      designCollectionId: urlDesignCollectionId,
      designTab: tab,
    });
  }

  function openCreativePreset(presetId: string, tab: PresetDetailTab = 'overview'): void {
    openCreativePresetForCollection(urlDesignCollectionId || selectedDesignCollectionId, presetId, tab);
  }

  function openCreativePresetForCollection(
    collectionId: string,
    presetId: string,
    tab: PresetDetailTab = 'overview',
  ): void {
    if (collectionId) setSelectedDesignCollectionId(collectionId);
    setUrlState({
      adTab: 'platforms',
      platformId: urlPlatformId || selectedNetworkId,
      platformTab: 'design',
      designCollectionId: collectionId || urlDesignCollectionId || selectedDesignCollectionId,
      designTab: 'creative-types',
      presetId,
      presetTab: tab,
    });
  }

  function closeCreativePreset(): void {
    setUrlState({
      adTab: 'platforms',
      platformId: urlPlatformId || selectedNetworkId,
      platformTab: 'design',
      designCollectionId: urlDesignCollectionId || selectedDesignCollectionId,
      designTab: 'creative-types',
      presetId: null,
      presetTab: null,
    });
  }

  function changeCreativePresetTab(tab: PresetDetailTab): void {
    setUrlState({
      adTab: 'platforms',
      platformId: urlPlatformId || selectedNetworkId,
      platformTab: 'design',
      designCollectionId: urlDesignCollectionId || selectedDesignCollectionId,
      designTab: 'creative-types',
      presetId: urlCreativePresetId,
      presetTab: tab,
    });
  }

  if (showStoryboard && urlBatchId && urlBatchItemId) {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <StoryboardWorkspace
          projectId={projectId}
          briefId={urlBatchId}
          itemId={urlBatchItemId}
          sceneIndex={urlSceneIndex}
          client={client}
          onBack={() => setBatchUrlState({ storyboard: false })}
          onOpenScene={(index) => setBatchUrlState({ scene: index })}
        />
      </Box>
    );
  }

  if (showCreativeDecisionRefreshDraft) {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <CreativeDecisionRefreshDraftPage
          projectId={projectId}
          client={client}
          parseResult={creativeDecisionRefreshParse}
          onBack={() => navigate(`/project/${encodeURIComponent(projectId)}/reports/creative-decisions`)}
          onOpenBatch={(briefId) => navigate(
            `/project/${encodeURIComponent(projectId)}/content-plan/creatives?batch=${encodeURIComponent(briefId)}&step=briefs`,
          )}
        />
      </Box>
    );
  }

  if (urlBatchId) {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <AdCreativeBatchWorkspace
          projectId={projectId}
          briefId={urlBatchId}
          client={client}
          step={urlBatchStep}
          selectedItemId={urlBatchItemId}
          onStepChange={handleBatchStepChange}
          onSelectItem={handleBatchSelectItem}
          onBack={handleBatchBack}
          onOpenStoryboard={handleBatchOpenStoryboard}
          initialLocale={localizedVariationHandoff.status === 'valid'
            && localizedVariationHandoff.handoff.creativeItemId === urlBatchItemId
            ? localizedVariationHandoff.handoff.locale
            : undefined}
        />
      </Box>
    );
  }

  function setViewParam(view: string | null): void {
    const next = new URLSearchParams(searchParams);
    ['adTab', 'brief', 'tab', 'briefTab', 'adPlatform', 'platformTab', 'designCollection', 'designTab', 'creativePreset', 'presetTab'].forEach((key) => next.delete(key));
    if (view) next.set('view', view);
    else next.delete('view');
    setSearchParams(next, { replace: false });
  }

  if (showCreateHome) {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <AdCreativeCreateHome
          projectId={projectId}
          client={client}
          briefs={briefs}
          items={items}
          designCollections={designCollections}
          onOpenBatch={(briefId) => setBatchUrlState({ batchId: briefId })}
          onOpenView={(view) => setViewParam(view)}
          onRefresh={() => void refreshWorkspace()}
          onOpenInsights={() => navigate(`/project/${projectId}?tab=insides`)}
        />
      </Box>
    );
  }

  if (urlView === 'brand') {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <BrandStylesPage
          projectId={projectId}
          client={client}
          designCollections={designCollections}
          onBack={() => setViewParam('home')}
          onOpenStyle={(collectionId) => openDesignCollection(collectionId)}
          onRefresh={() => void refreshWorkspace()}
        />
      </Box>
    );
  }

  if (urlView === 'import') {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <MetaImportRefreshPage
          projectId={projectId}
          client={client}
          onBack={() => setViewParam('home')}
          onOpenIntegrations={() => navigate(`/project/${projectId}/integrations`)}
          onOpenBatch={(briefId) => setBatchUrlState({ batchId: briefId })}
        />
      </Box>
    );
  }

  if (urlView === 'library') {
    return (
      <Box data-testid={AD_CREATIVE.page} sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1480, mx: 'auto' }}>
        <Stack spacing={2}>
          {itemsError && (
            <Alert
              severity="error"
              action={(
                <Button
                  color="inherit"
                  size="small"
                  disabled={loading}
                  onClick={() => void refreshWorkspace()}
                >
                  Retry
                </Button>
              )}
            >
              The creative library could not be loaded. Existing Meta/Facebook ads were not changed. {itemsError}
            </Alert>
          )}
          {(!itemsError || items.length > 0) && (
            <AdCreativeLibraryPage
              projectId={projectId}
              client={client}
              briefs={briefs}
              items={items}
              onBack={() => setViewParam('home')}
              onOpenItem={(briefId, itemId) => {
                if (briefId) setBatchUrlState({ batchId: briefId, step: 'creatives', itemId });
              }}
              onOpenBatch={(briefId) => setBatchUrlState({ batchId: briefId })}
              onPlanBacklog={() => {
                selectWorkspaceTab('briefs');
                openBriefTopicGenerationDialog();
              }}
              onRefresh={() => void refreshWorkspace()}
              localizedVariationHandoff={localizedVariationHandoff}
            />
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-testid={AD_CREATIVE.page}
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        maxWidth: 1480,
        mx: 'auto',
        minHeight: activeTab === 'briefs' ? '100vh' : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {status && <Alert data-testid={AD_CREATIVE.status} severity="success">{status}</Alert>}
        {briefApplyToast && <Alert severity="success" onClose={() => setBriefApplyToast(null)}>{briefApplyToast}</Alert>}

        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            data-testid="ad-creative-back-to-home"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              ['adTab', 'brief', 'tab', 'briefTab', 'adPlatform', 'platformTab', 'designCollection', 'designTab', 'creativePreset', 'presetTab'].forEach((key) => next.delete(key));
              next.set('view', 'home');
              setSearchParams(next, { replace: false });
            }}
          >
            ← Create
          </Button>
        <Tabs
          value={activeTab}
          onChange={(_, value) => selectWorkspaceTab(value as WorkspaceTab)}
          variant="scrollable"
          scrollButtons="auto"
          data-testid={AD_CREATIVE.tabs}
          sx={{ minHeight: 'auto', pt: '1px', flexShrink: 0, flex: 1 }}
        >
          {AD_CREATIVE_TAB_CONFIG.map((tab) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                icon={<Icon />}
                iconPosition="start"
                label={tab.label}
                data-testid={AD_CREATIVE.tab(tab.id)}
                sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto' }}
              />
            );
          })}
        </Tabs>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            mt: 1,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: activeTab === 'briefs' ? 'hidden' : undefined,
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {activeTab === 'platforms' && (
              <PlatformsTab
                networks={platformOptions}
                selectedNetworkId={selectedNetworkId}
                loading={loading}
                onNetworkSelect={setSelectedNetworkId}
                onOpenPlatform={openAdPlatform}
              />
            )}
            {activeTab === 'briefs' && (
              <BriefsTopicsTab
                briefs={briefs}
                selectedBriefId={selectedBriefId}
                generationRunCount={briefGenerationRuns.length}
                running={running}
                onSelectBrief={(briefId) => openBriefTopic(briefId)}
                onOpenBatch={(briefId) => setBatchUrlState({ batchId: briefId })}
                onOpenGenerationHistory={() => setBriefGenerationHistoryOpen(true)}
                onGenerateTopics={openBriefTopicGenerationDialog}
                onAddManual={() => void createManualBrief()}
                onArchiveBriefs={(ids) => void archiveBriefTopics(ids)}
              />
            )}
          </Box>
        </Paper>
      </Stack>

      <CreativeDetailDialog
        projectId={projectId}
        client={client}
        item={selectedItem}
        onItemUpdated={(item) => {
          setSelectedItem(item);
          setItems((current) => current.map((entry) => entry.id === item.id ? item : entry));
        }}
        onClose={() => setSelectedItem(null)}
      />
      <SDialog
        open={briefGenerationHistoryOpen}
        onClose={() => setBriefGenerationHistoryOpen(false)}
        title="Brief/topic generation history"
        maxWidth="lg"
        fullWidth
        data-testid={AD_CREATIVE.briefGenerationHistoryDialog}
      >
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generated brief/topic candidates stay here until you review and apply selected items into the backlog.
          </Typography>
          <GenerationHistoryList
            runs={briefGenerationRuns}
            loading={briefGenerationRunsLoading}
            onRowClick={(run) => setReviewBriefGenerationRun(run)}
            emptyHint="No ad brief/topic generation runs yet. Click Generate briefs/topics to create candidates for review."
          />
        </Box>
      </SDialog>
      <GenerationReviewDialog<AdCreativeBrief>
        open={Boolean(reviewBriefGenerationRun)}
        onClose={() => setReviewBriefGenerationRun(null)}
        projectId={projectId}
        entity="adBriefTopic"
        run={reviewBriefGenerationRun}
        title="Review generated briefs/topics"
        titleSuffix={reviewBriefGenerationRun ? `${reviewBriefGenerationRun.generatedItemCount ?? 0} candidates` : undefined}
        applyLabel="Apply selected briefs/topics"
        allowApplyAppliedRuns
        onApply={async (acceptedItemIds) => {
          if (!reviewBriefGenerationRun) return;
          const response = await client.applyBriefTopics({
            projectId,
            generationId: reviewBriefGenerationRun.id,
            acceptedItemIds,
          });
          if (!response.success || !response.data) throw new Error(response.error || 'Failed to apply generated briefs/topics');
          setBriefApplyToast(`Applied ${response.data.appliedCount} briefs/topics · skipped ${response.data.skippedCount} · rejected ${response.data.rejectedCount}`);
          setBriefGenerationHistoryOpen(false);
          await refreshWorkspace();
        }}
        renderItem={(draft: GenerationDraft<AdCreativeBrief>) => {
          const data = draft.data ?? ({} as AdCreativeBrief);
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">{data.title || draft.id}</Typography>
                {data.networkId && <Chip size="small" variant="outlined" label={data.networkId} />}
                {data.creativeType && <Chip size="small" variant="outlined" label={humanizeId(data.creativeType)} />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {String((data.audience as any)?.name || 'Selected audience')} - {String((data.task as any)?.type || 'selected task')}
              </Typography>
              {String((data.task as any)?.painOrNeed || '').trim() && (
                <Typography variant="caption" color="text.secondary">{String((data.task as any)?.painOrNeed)}</Typography>
              )}
            </Stack>
          );
        }}
        renderItemDetails={(draft: GenerationDraft<AdCreativeBrief>) => {
          const data = draft.data ?? ({} as AdCreativeBrief);
          return (
            <ResultPanel title="Candidate details" rows={[
              ['Audience', String((data.audience as any)?.name || '')],
              ['Task', String((data.task as any)?.type || '')],
              ['Buying move', data.buyingMove || ''],
              ['Objective', data.objective || ''],
              ['KPI', data.kpi || ''],
              ['Design collection', data.designCollectionId || ''],
              ['Presets', (data.selectedDesignPresetIds ?? []).join(', ')],
            ]} />
          );
        }}
      />
      <AdPlatformWorkspaceDialog
        open={Boolean(urlPlatformId)}
        network={platformDetailNetwork}
        activeTab={platformDetailTab}
        designCollections={designCollections}
        templates={templates}
        loading={loading}
        running={running}
        selectedCollectionId={selectedDesignCollectionId}
        onClose={closeAdPlatform}
        onTabChange={changeAdPlatformTab}
        onCollectionSelect={(collectionId) => {
          setSelectedDesignCollectionId(collectionId);
          openDesignCollection(collectionId);
        }}
        onPresetOpen={(collectionId, presetId) => openCreativePresetForCollection(collectionId, presetId)}
        onNewDesignCollection={() => {
          startNewDesignCollection();
          openDesignCollection('__draft__', 'style');
        }}
        onGenerateDesignCollection={() => setDesignGenerationOpen(true)}
      />
      <DesignCollectionWorkspaceDialog
        open={Boolean(urlDesignCollectionId)}
        collection={designDetailCollection}
        designForm={designForm}
        templates={templates}
        selectedPresetIds={selectedPresetIds}
        running={running}
        detailTab={designDetailTab}
        onClose={closeDesignCollection}
        onDetailTabChange={changeDesignCollectionTab}
        onPresetToggle={togglePreset}
        onOpenPreset={openCreativePreset}
        onDesignFormChange={setDesignForm}
        onSaveDesignCollection={() => void saveDesignCollectionForm()}
      />
      <CreativeTypePresetDetailDialog
        open={Boolean(urlCreativePresetId)}
        preset={creativePresetDetail}
        designForm={designForm}
        collection={designDetailCollection}
        templates={templates}
        selected={Boolean(urlCreativePresetId && selectedPresetIds.includes(urlCreativePresetId))}
        running={running}
        detailTab={presetDetailTab}
        onClose={closeCreativePreset}
        onDetailTabChange={changeCreativePresetTab}
        onPresetToggle={() => {
          if (urlCreativePresetId) togglePreset(urlCreativePresetId);
        }}
        onDesignFormChange={setDesignForm}
        onSaveDesignCollection={() => void saveDesignCollectionForm()}
      />
      <DesignCollectionGenerationDialog
        open={designGenerationOpen}
        projectId={projectId}
        network={platformDetailNetwork}
        model={designGenerationModel}
        form={designGenerationForm}
        baseCollection={selectedDesignCollection}
        selectedPresets={selectedPresets}
        running={running}
        onClose={() => setDesignGenerationOpen(false)}
        onModelChange={(model) => {
          designGenerationModelTouchedRef.current = true;
          setDesignGenerationModel(model);
        }}
        onFormChange={setDesignGenerationForm}
        onGenerate={() => void generateDesignCollectionFromReferences()}
      />
      <BriefTopicDetailDialog
        open={Boolean(urlBriefId)}
        projectId={projectId}
        campaign={null}
        brief={briefDetailTopic}
        form={briefForm}
        sourcePaths={sourcePaths}
        sourceHydratedData={sourceHydratedData}
        fallbackSourceIds={fallbackSourceIds}
        fallbackSources={FALLBACK_SOURCE_OPTIONS}
        researchRun={researchRun}
        angles={angles}
        selectedCollection={selectedDesignCollection}
        selectedPresets={selectedPresets}
        items={activeBriefItems}
        running={running}
        generateAssets={generateAssets}
        imageModel={creativeImageModel}
        tab={briefDetailTab}
        onClose={closeBriefTopic}
        onTabChange={changeBriefTopicTab}
        onFormChange={setBriefForm}
        onSourcePathsChange={setSourcePaths}
        onHydratedSourceChange={setSourceHydratedData}
        onFallbackSourceToggle={toggleFallbackSource}
        onGenerateAssetsChange={setGenerateAssets}
        onImageModelChange={setCreativeImageModel}
        onRunResearch={() => void runResearchForSelectedBrief(briefDetailTopic, null)}
        onGenerateAngles={() => void generateAnglesForSelectedBrief(briefDetailTopic, null)}
        onGenerateCreatives={() => void planAndGenerate(briefDetailTopic, null)}
        onOpenItem={setSelectedItem}
      />
      <BriefTopicGenerationDialog
        open={briefTopicGenerationOpen}
        projectId={projectId}
        campaign={null}
        form={briefGenerationForm}
        model={briefGenerationModel}
        sourcePaths={briefGenerationSourcePaths}
        sourceHydratedData={briefGenerationSourceHydratedData}
        fallbackSourceIds={briefGenerationFallbackSourceIds}
        fallbackSources={FALLBACK_SOURCE_OPTIONS}
        selectedCollection={selectedDesignCollection}
        selectedPresets={selectedPresets}
        running={running}
        onClose={() => setBriefTopicGenerationOpen(false)}
        onFormFieldChange={(field, value) => {
          setBriefGenerationForm((current) => ({ ...current, [field]: value }));
        }}
        onModelChange={(model) => {
          briefGenerationModelTouchedRef.current = true;
          setBriefGenerationModel(model);
        }}
        onSourcePathsChange={setBriefGenerationSourcePaths}
        onHydratedSourceChange={setBriefGenerationSourceHydratedData}
        onFallbackSourceToggle={toggleBriefGenerationFallbackSource}
        onGenerate={() => void generateBriefTopics()}
      />
    </Box>
  );
}

function CampaignsTab({
  campaigns,
  networks,
  selectedCampaignId,
  selectedNetworkId,
  form,
  running,
  onFormChange,
  onSelect,
  onNetworkSelect,
  onCreate,
}: {
  campaigns: AdCreativeCampaign[];
  networks: AdNetwork[];
  selectedCampaignId: string;
  selectedNetworkId: string;
  form: CampaignForm;
  running: boolean;
  onFormChange: (form: CampaignForm) => void;
  onSelect: (campaignId: string) => void;
  onNetworkSelect: (networkId: string) => void;
  onCreate: () => void;
}): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '380px minmax(0, 1fr)' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h6">Campaign setup</Typography>
        <StyledTextField
          label="Advertising platform"
          select
          value={form.networkId || selectedNetworkId}
          onChange={(value) => {
            const networkId = String(value);
            onNetworkSelect(networkId);
            onFormChange({ ...form, networkId });
          }}
          fullWidth
        >
          {(networks.length > 0 ? networks : [{ id: DEFAULT_NETWORK_ID, displayName: 'Meta', isActive: true } as AdNetwork]).map((network) => (
            <MenuItem key={network.id} value={network.id}>{network.displayName}</MenuItem>
          ))}
        </StyledTextField>
        <StyledTextField
          data-testid={AD_CREATIVE.campaignName}
          label="Campaign"
          value={form.title}
          onChange={(value) => onFormChange({ ...form, title: String(value) })}
          fullWidth
        />
        <StyledTextField
          label="Objective"
          select
          value={form.objectiveId}
          onChange={(value) => onFormChange({ ...form, objectiveId: String(value) })}
          fullWidth
        >
          <MenuItem value="leads">Leads</MenuItem>
          <MenuItem value="sales">Sales</MenuItem>
          <MenuItem value="traffic">Traffic</MenuItem>
          <MenuItem value="awareness">Awareness</MenuItem>
        </StyledTextField>
        <StyledTextField
          label="Primary KPI"
          select
          value={form.primaryKpi}
          onChange={(value) => onFormChange({ ...form, primaryKpi: String(value) })}
          fullWidth
        >
          <MenuItem value="cpl">CPL</MenuItem>
          <MenuItem value="ctr">CTR</MenuItem>
          <MenuItem value="cpa">CPA</MenuItem>
          <MenuItem value="roas">ROAS</MenuItem>
        </StyledTextField>
        <StyledTextField
          label="Destination URL"
          value={form.destinationUrl}
          onChange={(value) => onFormChange({ ...form, destinationUrl: String(value) })}
          fullWidth
        />
        <SelectableChips
          label="Publisher platforms"
          options={['facebook', 'instagram']}
          selected={form.publisherPlatforms}
          onChange={(next) => onFormChange({ ...form, publisherPlatforms: next })}
        />
        <SelectableChips
          label="Placement families"
          options={['feed', 'story', 'reel', 'carousel']}
          selected={form.placementFamilies}
          onChange={(next) => onFormChange({ ...form, placementFamilies: next })}
        />
        <Button
          data-testid={AD_CREATIVE.createCampaign}
          variant="contained"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <CampaignIcon />}
          disabled={running}
          onClick={onCreate}
        >
          Create campaign
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Campaign collections</Typography>
          <Chip size="small" label={`${campaigns.length} campaigns`} />
        </Stack>
        <Box
          data-testid={AD_CREATIVE.campaignList}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {campaigns.length === 0 ? (
            <EmptyState title="No ad campaigns yet" detail="Create a paid campaign collection to hold briefs, design choices, generated creatives, launch plans, and learnings." />
          ) : campaigns.map((campaign) => (
            <ButtonBase
              key={campaign.id}
              data-testid={AD_CREATIVE.campaignCard(campaign.id)}
              onClick={() => onSelect(campaign.id)}
              sx={{
                border: 1,
                borderColor: campaign.id === selectedCampaignId ? 'primary.main' : 'divider',
                borderRadius: 1,
                alignItems: 'stretch',
                display: 'block',
                p: 1.5,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{campaign.title}</Typography>
                  <Chip size="small" color={campaign.status === 'draft' ? 'default' : 'primary'} label={campaign.status} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {campaign.networkId} - {campaign.objectiveId || 'objective'} - {campaign.primaryKpi || 'kpi'}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {campaign.selectedPublisherPlatforms?.map((platform) => <Chip key={platform} size="small" label={platform} />)}
                  {campaign.selectedPlacementFamilies?.map((placement) => <Chip key={placement} size="small" variant="outlined" label={placement} />)}
                </Stack>
              </Stack>
            </ButtonBase>
          ))}
        </Box>
      </Stack>
    </Box>
  );
}

function PlatformsTab({
  networks,
  selectedNetworkId,
  loading,
  onNetworkSelect,
  onOpenPlatform,
}: {
  networks: AdNetwork[];
  selectedNetworkId: string;
  loading: boolean;
  onNetworkSelect: (networkId: string) => void;
  onOpenPlatform: (networkId: string) => void;
}): React.ReactElement {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6">Platforms</Typography>
          <Typography variant="body2" color="text.secondary">
            Each advertising platform owns its design collections and creative-type presets.
          </Typography>
        </Box>
        {loading && <CircularProgress size={22} />}
      </Stack>
      <Box
        data-testid={AD_CREATIVE.platformList}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}
      >
        {networks.map((network) => (
          <ButtonBase
            key={network.id}
            data-testid={AD_CREATIVE.platformCard(network.id)}
            onClick={() => {
              onNetworkSelect(network.id);
              onOpenPlatform(network.id);
            }}
            sx={{
              border: 1,
              borderColor: selectedNetworkId === network.id ? 'primary.main' : 'divider',
              borderRadius: 1,
              display: 'block',
              p: 1.5,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                  <PublicIcon fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{network.displayName}</Typography>
                </Stack>
                <Chip size="small" color={network.isActive ? 'success' : 'default'} label={network.isActive ? 'active' : 'disabled'} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {network.provider || network.id} - {network.status || 'catalog ready'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {network.policySummary || 'Platform rules and placements are supplied by the ad creative catalog.'}
              </Typography>
            </Stack>
          </ButtonBase>
        ))}
      </Box>
    </Stack>
  );
}

function AdPlatformWorkspaceDialog({
  open,
  network,
  activeTab,
  designCollections,
  templates,
  loading,
  running,
  selectedCollectionId,
  onClose,
  onTabChange,
  onCollectionSelect,
  onPresetOpen,
  onNewDesignCollection,
  onGenerateDesignCollection,
}: {
  open: boolean;
  network: AdNetwork | null;
  activeTab: PlatformDetailTab;
  designCollections: AdCreativeDesignCollection[];
  templates: AdCreativeTemplate[];
  loading: boolean;
  running: boolean;
  selectedCollectionId: string;
  onClose: () => void;
  onTabChange: (tab: PlatformDetailTab) => void;
  onCollectionSelect: (collectionId: string) => void;
  onPresetOpen: (collectionId: string, presetId: string) => void;
  onNewDesignCollection: () => void;
  onGenerateDesignCollection: () => void;
}): React.ReactElement | null {
  if (!network) return null;
  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={`${network.displayName} workspace`}
      fullScreen
      fullWidth
      maxWidth="xl"
      data-testid={AD_CREATIVE.platformWorkspace}
    >
      <Box sx={{ px: 3, pb: 3, height: 'calc(100vh - 96px)', overflow: 'auto' }}>
        <PlatformDetailView
          network={network}
          activeTab={activeTab}
          designCollections={designCollections}
          templates={templates}
          loading={loading}
          running={running}
          selectedCollectionId={selectedCollectionId}
          onBack={onClose}
          onTabChange={onTabChange}
          onCollectionSelect={onCollectionSelect}
          onPresetOpen={onPresetOpen}
          onNewDesignCollection={onNewDesignCollection}
          onGenerateDesignCollection={onGenerateDesignCollection}
        />
      </Box>
    </SDialog>
  );
}

function PlatformDetailView({
  network,
  activeTab,
  designCollections,
  templates,
  loading,
  running,
  selectedCollectionId,
  onBack,
  onTabChange,
  onCollectionSelect,
  onPresetOpen,
  onNewDesignCollection,
  onGenerateDesignCollection,
}: {
  network: AdNetwork;
  activeTab: PlatformDetailTab;
  designCollections: AdCreativeDesignCollection[];
  templates: AdCreativeTemplate[];
  loading: boolean;
  running: boolean;
  selectedCollectionId: string;
  onBack: () => void;
  onTabChange: (tab: PlatformDetailTab) => void;
  onCollectionSelect: (collectionId: string) => void;
  onPresetOpen: (collectionId: string, presetId: string) => void;
  onNewDesignCollection: () => void;
  onGenerateDesignCollection: () => void;
}): React.ReactElement {
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box>
          <Button size="small" onClick={onBack}>Back to platforms</Button>
          <Typography variant="h6">{network.displayName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {network.provider || network.id} - {network.status || 'catalog ready'}
          </Typography>
        </Box>
        {loading && <CircularProgress size={22} />}
      </Stack>
      <Tabs
        value={activeTab}
        onChange={(_, value) => onTabChange(value as PlatformDetailTab)}
        variant="scrollable"
        scrollButtons="auto"
        data-testid={AD_CREATIVE.platformWorkspaceTabs}
      >
        <Tab value="config" label="Config" data-testid={AD_CREATIVE.platformWorkspaceTab('config')} />
        <Tab value="design" label="Design" data-testid={AD_CREATIVE.platformWorkspaceTab('design')} />
        <Tab value="templates" label="Templates" data-testid={AD_CREATIVE.platformWorkspaceTab('templates')} />
      </Tabs>
      {activeTab === 'config' && (
        <ResultPanel title="Platform configuration" rows={[
          ['Status', network.status || 'template_ready'],
          ['Provider', network.provider || network.id],
          ['Active', network.isActive ? 'yes' : 'no'],
          ['Policy', network.policySummary || 'Platform rules and placements are supplied by the ad creative catalog.'],
        ]} />
      )}
      {activeTab === 'design' && (
        <AdPlatformDesignTab
          network={network}
          designCollections={designCollections}
          selectedCollectionId={selectedCollectionId}
          running={running}
          onCollectionSelect={onCollectionSelect}
          onPresetOpen={onPresetOpen}
          onNewDesignCollection={onNewDesignCollection}
          onGenerateDesignCollection={onGenerateDesignCollection}
        />
      )}
      {activeTab === 'templates' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
          {templates.map((template) => (
            <Box key={template.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{template.name}</Typography>
              <Typography variant="caption" color="text.secondary">{humanizeId(template.creativeType)} - {template.formatId || template.id}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}

function AdPlatformDesignTab({
  network,
  designCollections,
  selectedCollectionId,
  running,
  onCollectionSelect,
  onPresetOpen,
  onNewDesignCollection,
  onGenerateDesignCollection,
}: {
  network: AdNetwork;
  designCollections: AdCreativeDesignCollection[];
  selectedCollectionId: string;
  running: boolean;
  onCollectionSelect: (collectionId: string) => void;
  onPresetOpen: (collectionId: string, presetId: string) => void;
  onNewDesignCollection: () => void;
  onGenerateDesignCollection: () => void;
}): React.ReactElement {
  const currentCollection = designCollections.find((collection) => collection.id === selectedCollectionId)
    ?? designCollections.find((collection) => collection.isCurrent)
    ?? designCollections[0]
    ?? null;
  const currentPresets = currentCollection?.presets ?? [];

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          Design system package
        </Typography>
        <Typography variant="body2" color="text.secondary">
          One platform-owned collection defines the paid-media visual system and adapts it across every supported creative type.
        </Typography>
      </Box>

      <Box
        data-testid={AD_CREATIVE.platformDesignPackageSummary}
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2">
                {currentCollection?.name ?? 'No package yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentCollection
                  ? currentCollection.description || `Collection id: ${currentCollection.id}`
                  : `No ${network.displayName} design collection has been created yet.`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color={currentCollection ? 'success' : 'warning'}
                label={currentCollection ? 'Current collection' : 'Empty'}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${currentPresets.length} creative slots`}
              />
              {currentCollection?.status && (
                <Chip size="small" variant="outlined" label={currentCollection.status} />
              )}
            </Stack>
          </Stack>
          {designCollections.length > 1 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {designCollections.map((collection) => (
                <Button
                  key={collection.id}
                  size="small"
                  variant={collection.id === currentCollection?.id ? 'contained' : 'outlined'}
                  onClick={() => onCollectionSelect(collection.id)}
                >
                  {collection.name}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Design collections</Typography>
          <Typography variant="body2" color="text.secondary">
            Open a collection to edit its style foundation and creative-type presets.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={`${designCollections.length} collections`} />
          <Button
            data-testid={AD_CREATIVE.newDesignCollection}
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={running}
            onClick={onNewDesignCollection}
          >
            New collection from current
          </Button>
          <Button
            data-testid={AD_CREATIVE.generateDesignCollection}
            size="small"
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            disabled={running}
            onClick={onGenerateDesignCollection}
          >
            Generate from references
          </Button>
        </Stack>
      </Stack>

      <Box
        data-testid={AD_CREATIVE.collectionList}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}
      >
        {designCollections.length === 0 ? (
          <EmptyState title="No design collections found" detail="Refresh the selected platform. The backend seeds project-owned collections from the platform template catalog." />
        ) : designCollections.map((collection) => {
          const selected = collection.id === currentCollection?.id;
          return (
            <ButtonBase
              key={collection.id}
              data-testid={AD_CREATIVE.collection(collection.id)}
              onClick={() => onCollectionSelect(collection.id)}
              sx={{
                border: 1,
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 1,
                display: 'block',
                p: 1.5,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{collection.name}</Typography>
                  <Chip size="small" color={selected ? 'primary' : 'default'} label={collection.status} />
                </Stack>
                <DesignMockup collection={collection} />
                <Typography variant="body2" color="text.secondary">{collection.description}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {(collection.presets ?? []).slice(0, 6).map((preset) => (
                    <Chip key={preset.id} size="small" variant="outlined" label={humanizeId(preset.creativeType)} />
                  ))}
                </Stack>
              </Stack>
            </ButtonBase>
          );
        })}
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={700}>Creative-type slots</Typography>
        <Typography variant="body2" color="text.secondary">
          These slots mirror post-type design slots in Content Plan: each creative type can carry its own templates, rules, and generation defaults.
        </Typography>
      </Box>
      <Box
        data-testid={AD_CREATIVE.platformDesignSlotMatrix}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 1.5,
        }}
      >
        {!currentCollection ? (
          <EmptyState title="No current design collection" detail="Create or select a collection before editing creative-type slots." />
        ) : currentPresets.map((preset) => (
          <Box
            key={preset.id}
            role="button"
            tabIndex={0}
            data-testid={AD_CREATIVE.platformDesignSlot(preset.id)}
            onClick={() => onPresetOpen(currentCollection.id, preset.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onPresetOpen(currentCollection.id, preset.id);
              }
            }}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
              minHeight: 220,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
              '&:focus-visible': {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2">{preset.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {humanizeId(preset.creativeType)} - {(preset.placementFamilies ?? []).join(', ') || 'platform placements'}
                </Typography>
              </Box>
              <Chip
                size="small"
                color={preset.status === 'reviewed' ? 'success' : preset.status === 'archived' ? 'default' : 'warning'}
                label={preset.status ?? 'draft'}
                variant={preset.status === 'reviewed' ? 'filled' : 'outlined'}
              />
            </Stack>
            <DesignMockup collection={{
              ...currentCollection,
              name: preset.name,
              presets: [preset],
            } as AdCreativeDesignCollection} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {preset.description || 'Open to review templates, creative rules, and generation settings for this slot.'}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
              <Chip size="small" variant="outlined" label={`${preset.templateIds?.length ?? 0} templates`} />
              <Chip size="small" variant="outlined" label={`${Number((preset as any).generationDefaults?.variantCount ?? 1)} variants`} />
              <Chip size="small" variant="outlined" label={preset.sourceProfileKey || 'collection style'} />
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

function DesignCollectionGenerationDialog({
  open,
  projectId,
  network,
  model,
  form,
  baseCollection,
  selectedPresets,
  running,
  onClose,
  onModelChange,
  onFormChange,
  onGenerate,
}: {
  open: boolean;
  projectId: string;
  network: AdNetwork | null;
  model: string;
  form: DesignGenerationForm;
  baseCollection: AdCreativeDesignCollection | null;
  selectedPresets: AdCreativeDesignPreset[];
  running: boolean;
  onClose: () => void;
  onModelChange: (model: string) => void;
  onFormChange: (form: DesignGenerationForm) => void;
  onGenerate: () => void;
}): React.ReactElement {
  const referenceCount = parseDesignReferenceNotes(form.referenceNotes).length;
  const canGenerate = Boolean(projectId && network?.id && model && form.sourceBrief.trim() && referenceCount > 0);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid={AD_CREATIVE.generateDesignCollectionDialog}
    >
      <DialogTitle>Generate design collection from references</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            This starts a backend task. The generated collection is written into the selected platform design collections and can become the current design system after review.
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' }, gap: 2 }}>
            <Stack spacing={1.5}>
              <AvailableModelSelector
                projectId={projectId}
                value={model}
                onChange={onModelChange}
                label="Design-system generator"
                sx={{ width: '100%' }}
              />
              <StyledTextField
                label="Collection name"
                value={form.name}
                onChange={(value) => onFormChange({ ...form, name: String(value) })}
                fullWidth
              />
              <StyledTextField
                label="Description"
                value={form.description}
                onChange={(value) => onFormChange({ ...form, description: String(value) })}
                fullWidth
                multiline
                minRows={2}
              />
              <StyledTextField
                label="Design brief"
                value={form.sourceBrief}
                onChange={(value) => onFormChange({ ...form, sourceBrief: String(value) })}
                fullWidth
                multiline
                minRows={4}
              />
              <StyledTextField
                label="Reference notes"
                value={form.referenceNotes}
                onChange={(value) => onFormChange({ ...form, referenceNotes: String(value) })}
                fullWidth
                multiline
                minRows={6}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.setCurrent}
                    onChange={(event) => onFormChange({ ...form, setCurrent: event.target.checked })}
                  />
                }
                label="Make generated collection current"
              />
            </Stack>
            <Stack spacing={1.5}>
              <ResultPanel title="Generation context" rows={[
                ['Platform', network?.displayName || 'Not selected'],
                ['Base collection', baseCollection?.name || 'Current platform default'],
                ['Selected creative types', selectedPresets.length > 0 ? selectedPresets.map((preset) => humanizeId(preset.creativeType)).join(', ') : 'All platform creative types'],
                ['Reference notes', String(referenceCount)],
                ['Task', 'ad-creative-design-collection-generation'],
              ]} />
              <Alert severity={canGenerate ? 'success' : 'warning'}>
                {canGenerate
                  ? 'Ready to generate a platform-owned design collection through backend tasks.'
                  : 'Add a design brief and at least one reference note before generation.'}
              </Alert>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          data-testid={AD_CREATIVE.generateDesignCollectionSubmit}
          variant="contained"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          disabled={running || !canGenerate}
          onClick={onGenerate}
        >
          Generate design collection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DesignCollectionWorkspaceDialog({
  open,
  collection,
  designForm,
  templates,
  selectedPresetIds,
  running,
  detailTab,
  onClose,
  onDetailTabChange,
  onPresetToggle,
  onOpenPreset,
  onDesignFormChange,
  onSaveDesignCollection,
}: {
  open: boolean;
  collection: AdCreativeDesignCollection | null;
  designForm: DesignCollectionForm | null;
  templates: AdCreativeTemplate[];
  selectedPresetIds: string[];
  running: boolean;
  detailTab: DesignDetailTab;
  onClose: () => void;
  onDetailTabChange: (tab: DesignDetailTab) => void;
  onPresetToggle: (presetId: string) => void;
  onOpenPreset: (presetId: string) => void;
  onDesignFormChange: (form: DesignCollectionForm) => void;
  onSaveDesignCollection: () => void;
}): React.ReactElement {
  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={`${collection?.name ?? designForm?.name ?? 'Design collection'} workspace`}
      fullScreen
      fullWidth
      maxWidth="xl"
      data-testid={AD_CREATIVE.designCollectionWorkspace}
    >
      <Box sx={{ px: 3, pb: 3, height: 'calc(100vh - 96px)', overflow: 'auto' }}>
        <DesignDetailView
          collection={collection}
          designForm={designForm}
          templates={templates}
          selectedPresetIds={selectedPresetIds}
          running={running}
          detailTab={detailTab}
          onBack={onClose}
          onDetailTabChange={onDetailTabChange}
          onPresetToggle={onPresetToggle}
          onOpenPreset={onOpenPreset}
          onDesignFormChange={onDesignFormChange}
          onSaveDesignCollection={onSaveDesignCollection}
        />
      </Box>
    </SDialog>
  );
}

function DesignDetailView({
  collection,
  designForm,
  templates,
  selectedPresetIds,
  running,
  detailTab,
  onBack,
  onDetailTabChange,
  onPresetToggle,
  onOpenPreset,
  onDesignFormChange,
  onSaveDesignCollection,
}: {
  collection: AdCreativeDesignCollection | null;
  designForm: DesignCollectionForm | null;
  templates: AdCreativeTemplate[];
  selectedPresetIds: string[];
  running: boolean;
  detailTab: DesignDetailTab;
  onBack: () => void;
  onDetailTabChange: (tab: DesignDetailTab) => void;
  onPresetToggle: (presetId: string) => void;
  onOpenPreset: (presetId: string) => void;
  onDesignFormChange: (form: DesignCollectionForm) => void;
  onSaveDesignCollection: () => void;
}): React.ReactElement {
  const templateById = new Map(templates.map((template) => [template.id, template]));
  if (!collection) {
    return (
      <Stack spacing={1}>
        <Button size="small" onClick={onBack}>Back to platform</Button>
        <EmptyState title="Design collection not found" detail="Go back to the platform designs list and select another collection." />
      </Stack>
    );
  }
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box>
          <Button size="small" onClick={onBack}>Back to platform design</Button>
          <Typography variant="h6">{collection.name}</Typography>
          <Typography variant="body2" color="text.secondary">{collection.description}</Typography>
        </Box>
        <Chip size="small" label={collection.status} />
      </Stack>
      <Tabs
        value={detailTab}
        onChange={(_, value) => onDetailTabChange(value as DesignDetailTab)}
        variant="scrollable"
        scrollButtons="auto"
        data-testid={AD_CREATIVE.designCollectionDetailTabs}
      >
        <Tab value="overview" label="Overview" data-testid={AD_CREATIVE.designCollectionDetailTab('overview')} />
        <Tab value="style" label="Style" data-testid={AD_CREATIVE.designCollectionDetailTab('style')} />
        <Tab value="creative-types" label="Creative types" data-testid={AD_CREATIVE.designCollectionDetailTab('creative-types')} />
      </Tabs>
      {detailTab === 'overview' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '420px minmax(0, 1fr)' }, gap: 2 }}>
          <DesignMockup collection={collection} />
          <ResultPanel title="Design summary" rows={[
            ['Collection', collection.name],
            ['Status', collection.status],
            ['Current', collection.isCurrent ? 'yes' : 'no'],
            ['Presets', String((collection.presets ?? []).length)],
            ['Palette', Array.isArray(collection.styleAnchor?.palette) ? collection.styleAnchor?.palette.join(', ') : 'Not configured'],
          ]} />
        </Box>
      )}
      {detailTab === 'style' && designForm && (
        <DesignCollectionSettings
          form={designForm}
          running={running}
          onChange={onDesignFormChange}
          onSave={onSaveDesignCollection}
        />
      )}
      {detailTab === 'creative-types' && (
        <FormGroup
          data-testid={AD_CREATIVE.templateGallery}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}
        >
          {(collection.presets ?? []).map((preset) => (
            <ButtonBase
              key={preset.id}
              data-testid={AD_CREATIVE.presetCard(preset.id)}
              onClick={() => onOpenPreset(preset.id)}
              sx={{
                border: 1,
                borderColor: selectedPresetIds.includes(preset.id) ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 1,
                display: 'block',
                textAlign: 'left',
                width: '100%',
              }}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selectedPresetIds.includes(preset.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => onPresetToggle(preset.id)}
                  />
                )}
                label={(
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{preset.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {humanizeId(preset.creativeType)} - {(preset.placementFamilies ?? []).join(', ') || 'platform placements'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {(preset.templateIds ?? []).map((id) => templateById.get(id)?.name || id).join(', ')}
                    </Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
              />
            </ButtonBase>
          ))}
        </FormGroup>
      )}
    </Stack>
  );
}

function DesignCollectionSettings({
  form,
  running,
  onChange,
  onSave,
}: {
  form: DesignCollectionForm;
  running: boolean;
  onChange: (form: DesignCollectionForm) => void;
  onSave: () => void;
}): React.ReactElement {
  return (
    <Stack data-testid={AD_CREATIVE.designSettings} spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="h6">Design collection settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the visual foundation and the creative-type presets owned by this platform collection.
          </Typography>
        </Box>
        <Button
          data-testid={AD_CREATIVE.saveDesignCollection}
          variant="contained"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          disabled={running || !form.name.trim()}
          onClick={onSave}
        >
          Save design collection
        </Button>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px' }, gap: 1.5 }}>
        <Stack spacing={1.25}>
          <StyledTextField
            data-testid={AD_CREATIVE.designCollectionName}
            label="Collection name"
            value={form.name}
            onChange={(value) => onChange({ ...form, name: String(value) })}
            fullWidth
          />
          <StyledTextField
            label="Description"
            value={form.description}
            onChange={(value) => onChange({ ...form, description: String(value) })}
            fullWidth
            multiline
            minRows={2}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            <StyledTextField
              data-testid={AD_CREATIVE.designCollectionPalette}
              label="Palette"
              value={form.palette}
              onChange={(value) => onChange({ ...form, palette: String(value) })}
              fullWidth
            />
            <StyledTextField
              data-testid={AD_CREATIVE.designCollectionTypography}
              label="Typography"
              value={form.typography}
              onChange={(value) => onChange({ ...form, typography: String(value) })}
              fullWidth
            />
            <StyledTextField
              data-testid={AD_CREATIVE.designCollectionComposition}
              label="Composition"
              value={form.composition}
              onChange={(value) => onChange({ ...form, composition: String(value) })}
              fullWidth
            />
            <StyledTextField
              label="Text policy"
              value={form.textPolicy}
              onChange={(value) => onChange({ ...form, textPolicy: String(value) })}
              fullWidth
            />
          </Box>
          <StyledTextField
            label="Reference policy"
            value={form.referencePolicy}
            onChange={(value) => onChange({ ...form, referencePolicy: String(value) })}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>

        <Stack spacing={1}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
            <FormControlLabel
              control={(
                <Switch
                  data-testid={AD_CREATIVE.designCollectionCurrent}
                  checked={form.isCurrent}
                  onChange={(event) => onChange({ ...form, isCurrent: event.target.checked })}
                />
              )}
              label="Current platform design"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Current design is the default inherited design when a brief/topic does not override style anchor.
            </Typography>
          </Box>
          <DesignMockup collection={designFormPreview(form)} />
        </Stack>
      </Box>
    </Stack>
  );
}

function CreativeTypePresetDetailDialog({
  open,
  preset,
  designForm,
  collection,
  templates,
  selected,
  running,
  detailTab,
  onClose,
  onDetailTabChange,
  onPresetToggle,
  onDesignFormChange,
  onSaveDesignCollection,
}: {
  open: boolean;
  preset: DesignPresetForm | AdCreativeDesignPreset | null;
  designForm: DesignCollectionForm | null;
  collection: AdCreativeDesignCollection | null;
  templates: AdCreativeTemplate[];
  selected: boolean;
  running: boolean;
  detailTab: PresetDetailTab;
  onClose: () => void;
  onDetailTabChange: (tab: PresetDetailTab) => void;
  onPresetToggle: () => void;
  onDesignFormChange: (form: DesignCollectionForm) => void;
  onSaveDesignCollection: () => void;
}): React.ReactElement {
  const editablePreset = preset && designForm
    ? designForm.presets.find((item) => item.id === preset.id) ?? null
    : null;
  const displayedPreset = editablePreset ?? preset;
  const typeTemplates = templates.filter((template) => template.creativeType === displayedPreset?.creativeType);
  const selectedTemplateNames = (displayedPreset?.templateIds ?? [])
    .map((id) => templates.find((template) => template.id === id)?.name ?? id);
  const displayedEnabled = editablePreset?.enabled
    ?? (displayedPreset ? String((displayedPreset as any).status ?? '') !== 'archived' : false);
  const displayedSlotLimit = editablePreset?.slotLimitPerCreative
    ?? Number((displayedPreset as any)?.generationDefaults?.slotLimitPerCreative
      ?? (displayedPreset as any)?.slotPolicies?.slotLimitPerCreative
      ?? 1);
  const styleAnchor = recordValue(editablePreset?.styleAnchor ?? (displayedPreset as any)?.styleAnchor);
  const referenceStrategy = recordValue(editablePreset?.referenceStrategy ?? (displayedPreset as any)?.referenceStrategy);
  const brandStyleKit = recordValue(editablePreset?.brandStyleKit ?? (displayedPreset as any)?.brandStyleKit);
  const generationDefaults = recordValue(editablePreset?.generationDefaults ?? (displayedPreset as any)?.generationDefaults);
  const slotPolicies = recordValue(editablePreset?.slotPolicies ?? (displayedPreset as any)?.slotPolicies);
  const previewSpec = recordValue(editablePreset?.previewSpec ?? (displayedPreset as any)?.previewSpec);
  const placementFamilies = editablePreset?.placementFamilies ?? displayedPreset?.placementFamilies ?? [];
  const aspectRatios = editablePreset?.aspectRatios ?? displayedPreset?.aspectRatios ?? [];

  const updatePreset = (patch: Partial<DesignPresetForm>) => {
    if (!designForm || !displayedPreset) return;
    onDesignFormChange({
      ...designForm,
      presets: designForm.presets.map((item) => (
        item.id === displayedPreset.id ? { ...item, ...patch } : item
      )),
    });
  };
  const updateStyleAnchor = (patch: Record<string, unknown>) => updatePreset({ styleAnchor: { ...styleAnchor, ...patch } });
  const updateReferenceStrategy = (patch: Record<string, unknown>) => updatePreset({ referenceStrategy: { ...referenceStrategy, ...patch } });

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={displayedPreset?.name ?? 'Creative type preset'}
      fullScreen
      fullWidth
      maxWidth="xl"
      data-testid={AD_CREATIVE.creativeTypePresetDialog}
      primaryAction={displayedPreset && designForm ? {
        label: 'Save design collection',
        onClick: onSaveDesignCollection,
        loading: running,
        disabled: running || !designForm.name.trim(),
        startIcon: <SaveIcon />,
        'data-testid': AD_CREATIVE.saveDesignCollection,
      } : undefined}
    >
      <Box sx={{ px: 3, pb: 3, height: 'calc(100vh - 96px)', overflow: 'auto' }}>
        {!displayedPreset ? (
          <EmptyState title="Creative type preset not found" detail="Go back to the design collection and select another creative type." />
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Box>
                <Button size="small" onClick={onClose}>Back to creative types</Button>
                <Typography variant="h6">{displayedPreset.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {humanizeId(displayedPreset.creativeType)} in {collection?.name ?? designForm?.name ?? 'selected design collection'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" color={selected ? 'primary' : 'default'} label={selected ? 'Selected for generation' : 'Not selected'} />
                <Chip size="small" color={displayedEnabled ? 'success' : 'default'} label={displayedEnabled ? 'enabled' : 'disabled'} />
                <Chip size="small" variant="outlined" label={`${displayedPreset.templateIds?.length ?? 0} templates`} />
              </Stack>
            </Stack>

            <Tabs
              value={detailTab}
              onChange={(_, value) => onDetailTabChange(value as PresetDetailTab)}
              variant="scrollable"
              scrollButtons="auto"
              data-testid={AD_CREATIVE.creativeTypePresetTabs}
            >
              <Tab value="overview" label="Overview" data-testid={AD_CREATIVE.creativeTypePresetTab('overview')} />
              <Tab value="settings" label="Settings" data-testid={AD_CREATIVE.creativeTypePresetTab('settings')} />
              <Tab value="templates" label="Templates" data-testid={AD_CREATIVE.creativeTypePresetTab('templates')} />
              <Tab value="rules" label="Rules" data-testid={AD_CREATIVE.creativeTypePresetTab('rules')} />
            </Tabs>

            {detailTab === 'overview' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '420px minmax(0, 1fr)' }, gap: 2 }}>
                <DesignMockup collection={collection ?? (designForm ? designFormPreview(designForm) : null)} />
                <Stack spacing={1.25}>
                  <ResultPanel title="Creative type summary" rows={[
                    ['Creative type', humanizeId(displayedPreset.creativeType)],
                    ['Preset', displayedPreset.name],
                    ['Status', displayedEnabled ? 'enabled' : 'disabled'],
                    ['Selected', selected ? 'yes' : 'no'],
                    ['Slot limit', String(displayedSlotLimit)],
                    ['Templates', selectedTemplateNames.join(', ') || 'No templates selected'],
                  ]} />
                  <ResultPanel title="Design system profile" rows={[
                    ['Source profile', displayedPreset.sourceProfileKey || 'Inherited from collection'],
                    ['Palette', formatList(styleAnchor.palette ?? collection?.styleAnchor?.palette)],
                    ['Typography', String(styleAnchor.typography ?? collection?.styleAnchor?.typography ?? 'Inherited from collection')],
                    ['Composition', String(styleAnchor.composition ?? collection?.styleAnchor?.composition ?? 'Inherited from collection')],
                    ['Text policy', String(styleAnchor.textPolicy ?? collection?.styleAnchor?.textPolicy ?? 'Inherited from collection')],
                    ['Aspect ratios', aspectRatios.join(', ') || formatList(previewSpec.ratios) || 'Template controlled'],
                    ['Reference strategy', formatList(referenceStrategy.requiredRefs) || String(referenceStrategy.sourcePolicy ?? 'Inherited from collection')],
                    ['Brand style kit', recordSummary(brandStyleKit)],
                  ]} />
                </Stack>
              </Box>
            )}

            {detailTab === 'settings' && (
              <Stack spacing={1.5} data-testid={AD_CREATIVE.detailSettings}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        data-testid={AD_CREATIVE.designPresetEnabled(displayedPreset.id)}
                        checked={displayedEnabled}
                        onChange={(event) => updatePreset({ enabled: event.target.checked })}
                        disabled={!editablePreset || running}
                      />
                    )}
                    label="Enabled in this design collection"
                  />
                  <FormControlLabel
                    control={<Switch checked={selected} onChange={onPresetToggle} disabled={running} />}
                    label="Use for generation"
                  />
                </Stack>
                <StyledTextField
                  label="Preset name"
                  value={editablePreset?.name ?? displayedPreset.name}
                  onChange={(value) => updatePreset({ name: String(value) })}
                  fullWidth
                  disabled={!editablePreset}
                />
                <StyledTextField
                  label="Preset description"
                  value={editablePreset?.description ?? displayedPreset.description ?? ''}
                  onChange={(value) => updatePreset({ description: String(value) })}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={!editablePreset}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px minmax(0, 1fr)' }, gap: 1 }}>
                  <StyledTextField
                    data-testid={AD_CREATIVE.designPresetSlotLimit(displayedPreset.id)}
                    label="Slot limit"
                    value={String(displayedSlotLimit)}
                    onChange={(value) => {
                      const nextLimit = Math.max(1, Number(value) || 1);
                      updatePreset({
                        slotLimitPerCreative: nextLimit,
                        generationDefaults: { ...generationDefaults, slotLimitPerCreative: nextLimit },
                        slotPolicies: { ...slotPolicies, slotLimitPerCreative: nextLimit },
                      });
                    }}
                    fullWidth
                    disabled={!editablePreset}
                  />
                  <StyledTextField
                    label="Source profile"
                    value={editablePreset?.sourceProfileKey ?? displayedPreset.sourceProfileKey ?? ''}
                    onChange={(value) => updatePreset({ sourceProfileKey: String(value) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Design system profile</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These fields mirror the per-post-type design profile in Content Plan, but are scoped to one paid creative type.
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                  <StyledTextField
                    label="Palette"
                    value={formatList(styleAnchor.palette)}
                    onChange={(value) => updateStyleAnchor({ palette: textToList(String(value)) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                  <StyledTextField
                    label="Typography"
                    value={String(styleAnchor.typography ?? '')}
                    onChange={(value) => updateStyleAnchor({ typography: String(value) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                  <StyledTextField
                    label="Composition"
                    value={String(styleAnchor.composition ?? '')}
                    onChange={(value) => updateStyleAnchor({ composition: String(value) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                  <StyledTextField
                    label="Text policy"
                    value={String(styleAnchor.textPolicy ?? '')}
                    onChange={(value) => updateStyleAnchor({ textPolicy: String(value) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                  <StyledTextField
                    label="Aspect ratios"
                    value={aspectRatios.join(', ')}
                    onChange={(value) => updatePreset({ aspectRatios: textToList(String(value)) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                  <StyledTextField
                    label="Placement families"
                    value={placementFamilies.join(', ')}
                    onChange={(value) => updatePreset({ placementFamilies: textToList(String(value)) })}
                    fullWidth
                    disabled={!editablePreset}
                  />
                </Box>
                <StyledTextField
                  label="Reference strategy"
                  value={formatList(referenceStrategy.requiredRefs ?? referenceStrategy.requiredReferences ?? '')}
                  onChange={(value) => updateReferenceStrategy({ requiredRefs: textToList(String(value)), sourcePolicy: 'reference_only' })}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={!editablePreset}
                />
              </Stack>
            )}

            {detailTab === 'templates' && (
              <Stack spacing={1.5} data-testid={AD_CREATIVE.templateGallery}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Templates for this creative type</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose platform-owned templates that can participate when this preset is used for generation.
                </Typography>
                <TemplateChipSelector
                  templates={typeTemplates}
                  selectedIds={editablePreset?.templateIds ?? displayedPreset.templateIds ?? []}
                  onChange={(templateIds) => updatePreset({ templateIds })}
                />
              </Stack>
            )}

            {detailTab === 'rules' && (
              <Stack spacing={1.25}>
                <ResultPanel title="Generation and placement rules" rows={[
                  ['Placement families', placementFamilies.join(', ') || 'platform placements'],
                  ['Placement ids', (editablePreset?.placementIds ?? displayedPreset.placementIds ?? []).join(', ') || 'Template controlled'],
                  ['Creative type', humanizeId(displayedPreset.creativeType)],
                  ['Format source', displayedPreset.sourceProfileKey || 'Not configured'],
                  ['Template count', String(displayedPreset.templateIds?.length ?? 0)],
                  ['Text policy', String(styleAnchor.textPolicy ?? collection?.styleAnchor?.textPolicy ?? designForm?.textPolicy ?? 'Inherited from design collection')],
                  ['Reference policy', String(referenceStrategy.sourcePolicy ?? collection?.referenceStrategy?.sourcePolicy ?? 'Inherited from design collection')],
                ]} />
                <ResultPanel title="Prompt-facing design objects" rows={[
                  ['Style anchor', recordSummary(styleAnchor)],
                  ['Brand style kit', recordSummary(brandStyleKit)],
                  ['Reference strategy', recordSummary(referenceStrategy)],
                  ['Slot policies', recordSummary(slotPolicies)],
                  ['Generation defaults', recordSummary(generationDefaults)],
                  ['Preview spec', recordSummary(previewSpec)],
                ]} />
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </SDialog>
  );
}

function TemplateChipSelector({
  templates,
  selectedIds,
  onChange,
}: {
  templates: AdCreativeTemplate[];
  selectedIds: string[];
  onChange: (templateIds: string[]) => void;
}): React.ReactElement {
  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary">Templates for this creative type</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap">
        {templates.length === 0 ? (
          <Chip size="small" label="No catalog templates" />
        ) : templates.map((template) => {
          const active = selectedIds.includes(template.id);
          return (
            <Chip
              key={template.id}
              size="small"
              label={template.name}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => onChange(active
                ? selectedIds.filter((id) => id !== template.id)
                : [...selectedIds, template.id])}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

function BriefsTopicsTab({
  briefs,
  selectedBriefId,
  generationRunCount,
  running,
  onSelectBrief,
  onOpenBatch,
  onOpenGenerationHistory,
  onGenerateTopics,
  onAddManual,
  onArchiveBriefs,
}: {
  briefs: AdCreativeBrief[];
  selectedBriefId: string;
  generationRunCount: number;
  running: boolean;
  onSelectBrief: (briefId: string) => void;
  onOpenBatch: (briefId: string) => void;
  onOpenGenerationHistory: () => void;
  onGenerateTopics: () => void;
  onAddManual: () => void;
  onArchiveBriefs: (briefIds: string[]) => void | Promise<void>;
}): React.ReactElement {
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const selectedIds = useMemo(
    () => Object.entries(rowSelection)
      .filter(([, selected]) => Boolean(selected))
      .map(([id]) => id),
    [rowSelection],
  );
  const readyCount = useMemo(
    () => briefs.filter((brief) => ['planned', 'ready'].includes(String(brief.status || ''))).length,
    [briefs],
  );
  const draftCount = useMemo(
    () => briefs.filter((brief) => !brief.status || String(brief.status) === 'draft').length,
    [briefs],
  );
  const columns = useMemo<SDataTableColumnDef<AdCreativeBrief>[]>(() => [
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => {
        const value = String(cell.getValue<string>() || 'draft');
        const ready = value === 'planned' || value === 'ready';
        return (
          <Chip
            label={value}
            size="small"
            color={ready ? 'success' : 'default'}
            variant={ready ? 'filled' : 'outlined'}
          />
        );
      },
      size: 110,
    },
    {
      accessorKey: 'title',
      header: 'Brief / Topic',
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', fontWeight: 600 }}>
          {row.original.title || 'Untitled brief'}
        </Typography>
      ),
      size: 300,
    },
    {
      id: 'audienceTask',
      header: 'Audience / Task',
      accessorFn: (row) => `${briefFieldText(row.audience, 'name')} ${briefFieldText(row.task, 'type')} ${briefFieldText(row.task, 'painOrNeed')}`,
      Cell: ({ row }) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
            {briefFieldText(row.original.audience, 'name') || 'No audience'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
            {briefFieldText(row.original.task, 'type') || briefFieldText(row.original.task, 'painOrNeed') || 'No task'}
          </Typography>
        </Stack>
      ),
      size: 300,
    },
    {
      id: 'offerProof',
      header: 'Offer / Proof',
      accessorFn: (row) => `${briefFieldText(row.productContext, 'offerPromise')} ${briefFieldText(row.productContext, 'proofSummary')}`,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          {briefFieldText(row.original.productContext, 'offerPromise')
            || briefFieldText(row.original.productContext, 'proofSummary')
            || '—'}
        </Typography>
      ),
      size: 320,
    },
    {
      accessorKey: 'designCollectionId',
      header: 'Design',
      Cell: ({ cell }) => (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          {cell.getValue<string>() || 'No design collection'}
        </Typography>
      ),
      size: 220,
    },
    {
      accessorKey: 'networkId',
      header: 'Platform',
      Cell: ({ cell }) => <Chip size="small" variant="outlined" label={cell.getValue<string>() || 'meta'} />,
      size: 120,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      Cell: ({ cell }) => (
        <Typography variant="body2" color="text.secondary">
          {formatAdCreativeDate(cell.getValue())}
        </Typography>
      ),
      size: 140,
    },
    {
      id: 'batch',
      header: '',
      Cell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          data-testid={`ad-creative-open-batch-${row.original.id}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenBatch(row.original.id);
          }}
        >
          Open batch
        </Button>
      ),
      size: 130,
    },
  ], [onOpenBatch]);

  async function handleArchiveSelected(): Promise<void> {
    if (selectedIds.length === 0) return;
    if (!confirm(`Archive ${selectedIds.length} brief topic(s)?`)) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onArchiveBriefs(selectedIds);
      setRowSelection({});
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : String(error));
    } finally {
      setDeleting(false);
    }
  }

  async function handleArchiveOne(briefId: string): Promise<void> {
    if (!confirm('Archive this brief topic?')) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onArchiveBriefs([briefId]);
      setRowSelection({});
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : String(error));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box data-testid={AD_CREATIVE.briefList} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ mb: 2, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="h6">Brief / topic backlog</Typography>
            <Typography variant="caption" color="text.secondary">
              {briefs.length} briefs · {readyCount} ready · {draftCount} draft
            </Typography>
          </Box>
          {selectedIds.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 2 }}>
              <Chip label={selectedIds.length} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
              <Tooltip title="Archive selected">
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    disabled={deleting || running}
                    onClick={() => void handleArchiveSelected()}
                    data-testid={AD_CREATIVE.briefBulkDelete}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button size="small" onClick={() => setRowSelection({})} sx={{ textTransform: 'none' }}>
                Clear selection
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            data-testid={AD_CREATIVE.briefGenerationHistory}
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={onOpenGenerationHistory}
          >
            Generation history
          </Button>
          <Button
            data-testid={AD_CREATIVE.generateBriefTopics}
            variant="contained"
            startIcon={running ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={running}
            onClick={onGenerateTopics}
          >
            Generate briefs/topics
          </Button>
          <Button
            data-testid={AD_CREATIVE.addBriefManually}
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={running}
            onClick={onAddManual}
          >
            + Add manually
          </Button>
        </Stack>
      </Stack>

      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}

      {briefs.length === 0 ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <EmptyState title="No ad brief topics yet" detail="Click Generate briefs/topics to populate the backlog via a dedicated setup flow." />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <SDataTable<AdCreativeBrief>
            columns={columns}
            data={briefs}
            getRowId={(row) => row.id ?? ''}
            enableRowSelection
            enableRowActions
            enableSorting
            enableTopToolbar={false}
            enableSearch
            searchInColumn="title"
            searchMode="global"
            searchPlaceholder="Search brief, audience, task, proof…"
            enableBottomToolbar
            enablePagination
            selectAllMode="all"
            initialState={{ pagination: { pageSize: 50, pageIndex: 0 } }}
            muiPaginationProps={{
              rowsPerPageOptions: [25, 50, 100],
              showFirstButton: true,
              showLastButton: true,
            }}
            enableStickyHeader
            state={{ rowSelection }}
            onRowSelectionChange={setRowSelection}
            muiTablePaperProps={{
              variant: 'outlined',
              elevation: 0,
              sx: {
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
            }}
            muiTableContainerProps={{
              sx: {
                flex: 1,
                minHeight: 0,
                height: '100%',
                maxHeight: 'none',
                overflow: 'auto',
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                bgcolor: 'action.hover',
                borderBottom: '2px solid',
                borderColor: 'divider',
                fontWeight: 'fontWeightMedium',
                position: 'sticky',
                top: 0,
                zIndex: 3,
              },
            }}
            muiTableProps={{
              size: 'small',
              sx: {
                minWidth: 1320,
                tableLayout: 'fixed',
                overflow: 'visible',
              },
            }}
            muiSelectCheckboxProps={{ size: 'small' }}
            muiSelectAllCheckboxProps={{ size: 'small' }}
            muiTableBodyRowProps={({ row }) => {
              const brief = row.original as AdCreativeBrief;
              const selected = brief.id === selectedBriefId;
              return {
                hover: true,
                'data-testid': AD_CREATIVE.briefCard(brief.id),
                onClick: (event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('.MuiCheckbox-root')) return;
                  if (target.closest('button')) return;
                  onSelectBrief(brief.id);
                },
                sx: {
                  cursor: 'pointer',
                  bgcolor: selected ? 'action.selected' : undefined,
                },
              };
            }}
            renderRowActionMenuItems={({ row, closeMenu }) => [
              <MenuItem
                key="open"
                onClick={() => {
                  closeMenu();
                  onSelectBrief((row.original as AdCreativeBrief).id);
                }}
              >
                <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
                Open detail
              </MenuItem>,
              <MenuItem
                key="archive"
                onClick={() => {
                  closeMenu();
                  void handleArchiveOne((row.original as AdCreativeBrief).id);
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Archive
              </MenuItem>,
            ]}
          />
        </Box>
      )}
    </Box>
  );
}

function BriefTopicDetailDialog({
  open,
  projectId,
  campaign,
  brief,
  form,
  sourcePaths,
  sourceHydratedData,
  fallbackSourceIds,
  fallbackSources,
  researchRun,
  angles,
  selectedCollection,
  selectedPresets,
  items,
  running,
  generateAssets,
  imageModel,
  tab: tabProp,
  onClose,
  onTabChange,
  onFormChange,
  onSourcePathsChange,
  onHydratedSourceChange,
  onFallbackSourceToggle,
  onGenerateAssetsChange,
  onImageModelChange,
  onRunResearch,
  onGenerateAngles,
  onGenerateCreatives,
  onOpenItem,
}: {
  open: boolean;
  projectId: string;
  campaign: AdCreativeCampaign | null;
  brief: AdCreativeBrief | null;
  form: BriefForm;
  sourcePaths: string[];
  sourceHydratedData: HydratedData | null;
  fallbackSourceIds: string[];
  fallbackSources: SourceOption[];
  researchRun: AdCreativeResearchRun | null;
  angles: Array<Record<string, unknown>>;
  selectedCollection: AdCreativeDesignCollection | null;
  selectedPresets: AdCreativeDesignPreset[];
  items: AdCreativeItem[];
  running: boolean;
  generateAssets: boolean;
  imageModel: string;
  tab?: BriefDetailTab;
  onClose: () => void;
  onTabChange?: (tab: BriefDetailTab) => void;
  onFormChange: (form: BriefForm) => void;
  onSourcePathsChange: (paths: string[]) => void;
  onHydratedSourceChange: (data: HydratedData) => void;
  onFallbackSourceToggle: (sourceId: string) => void;
  onGenerateAssetsChange: (value: boolean) => void;
  onImageModelChange: (model: string) => void;
  onRunResearch: () => void;
  onGenerateAngles: () => void;
  onGenerateCreatives: () => void;
  onOpenItem: (item: AdCreativeItem) => void;
}): React.ReactElement {
  const isControlled = tabProp !== undefined && typeof onTabChange === 'function';
  const [internalTab, setInternalTab] = useState<BriefDetailTab>('overview');
  const detailTab: BriefDetailTab = isControlled ? (tabProp as BriefDetailTab) : internalTab;
  const setDetailTab = (next: BriefDetailTab): void => {
    if (isControlled) {
      onTabChange!(next);
    } else {
      setInternalTab(next);
    }
  };

  useEffect(() => {
    if (isControlled) return;
    if (open) setInternalTab('overview');
  }, [brief?.id, isControlled, open]);

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={brief?.title || 'Brief / topic'}
      maxWidth="xl"
      fullWidth
      data-testid={AD_CREATIVE.selectedBriefWorkspace}
      PaperProps={{ sx: { height: '85vh', maxHeight: '85vh' } }}
    >
      {!brief ? (
        <EmptyState title="Brief/topic not found" detail="Close this workspace and pick another topic from the backlog." />
      ) : (
        <BriefTopicDetailView
          projectId={projectId}
          campaign={campaign}
          brief={brief}
          form={form}
          sourcePaths={sourcePaths}
          sourceHydratedData={sourceHydratedData}
          fallbackSourceIds={fallbackSourceIds}
          fallbackSources={fallbackSources}
          researchRun={researchRun}
          angles={angles}
          selectedCollection={selectedCollection}
          selectedPresets={selectedPresets}
          items={items}
          running={running}
          generateAssets={generateAssets}
          imageModel={imageModel}
          detailTab={detailTab}
          onClose={onClose}
          onDetailTabChange={setDetailTab}
          onFormChange={onFormChange}
          onSourcePathsChange={onSourcePathsChange}
          onHydratedSourceChange={onHydratedSourceChange}
          onFallbackSourceToggle={onFallbackSourceToggle}
          onGenerateAssetsChange={onGenerateAssetsChange}
          onImageModelChange={onImageModelChange}
          onRunResearch={onRunResearch}
          onGenerateAngles={onGenerateAngles}
          onGenerateCreatives={onGenerateCreatives}
          onOpenItem={onOpenItem}
        />
      )}
    </SDialog>
  );
}

function BriefTopicDetailView({
  projectId,
  campaign,
  brief,
  form,
  sourcePaths,
  sourceHydratedData,
  fallbackSourceIds,
  fallbackSources,
  researchRun,
  angles,
  selectedCollection,
  selectedPresets,
  items,
  running,
  generateAssets,
  imageModel,
  detailTab,
  onClose,
  onDetailTabChange,
  onFormChange,
  onSourcePathsChange,
  onHydratedSourceChange,
  onFallbackSourceToggle,
  onGenerateAssetsChange,
  onImageModelChange,
  onRunResearch,
  onGenerateAngles,
  onGenerateCreatives,
  onOpenItem,
}: {
  projectId: string;
  campaign: AdCreativeCampaign | null;
  brief: AdCreativeBrief;
  form: BriefForm;
  sourcePaths: string[];
  sourceHydratedData: HydratedData | null;
  fallbackSourceIds: string[];
  fallbackSources: SourceOption[];
  researchRun: AdCreativeResearchRun | null;
  angles: Array<Record<string, unknown>>;
  selectedCollection: AdCreativeDesignCollection | null;
  selectedPresets: AdCreativeDesignPreset[];
  items: AdCreativeItem[];
  running: boolean;
  generateAssets: boolean;
  imageModel: string;
  detailTab: BriefDetailTab;
  onClose: () => void;
  onDetailTabChange: (tab: BriefDetailTab) => void;
  onFormChange: (form: BriefForm) => void;
  onSourcePathsChange: (paths: string[]) => void;
  onHydratedSourceChange: (data: HydratedData) => void;
  onFallbackSourceToggle: (sourceId: string) => void;
  onGenerateAssetsChange: (value: boolean) => void;
  onImageModelChange: (model: string) => void;
  onRunResearch: () => void;
  onGenerateAngles: () => void;
  onGenerateCreatives: () => void;
  onOpenItem: (item: AdCreativeItem) => void;
}): React.ReactElement {
  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Button data-testid={AD_CREATIVE.briefDetailBack} size="small" onClick={onClose}>Back to brief backlog</Button>
          <Typography variant="h6" noWrap>{brief.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {brief.networkId} - {brief.objective || campaign?.objectiveId || 'objective'} - {brief.kpi || campaign?.primaryKpi || 'kpi'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap">
          <Chip size="small" label={brief.status || 'draft'} />
          <Chip size="small" variant="outlined" label={brief.designCollectionId || selectedCollection?.name || 'No design'} />
        </Stack>
      </Stack>
      <Tabs
        data-testid={AD_CREATIVE.briefDetailTabs}
        value={detailTab}
        onChange={(_, value) => onDetailTabChange(value as BriefDetailTab)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
      >
        <Tab value="overview" label="Overview" data-testid={AD_CREATIVE.briefDetailTab('overview')} />
        <Tab value="research" label="Research" data-testid={AD_CREATIVE.briefDetailTab('research')} />
        <Tab value="angles" label="Angles" data-testid={AD_CREATIVE.briefDetailTab('angles')} />
        <Tab value="design" label="Design" data-testid={AD_CREATIVE.briefDetailTab('design')} />
        <Tab value="creatives" label="Creatives" data-testid={AD_CREATIVE.briefDetailTab('creatives')} />
      </Tabs>
      <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto', pr: 1 }}>
        {detailTab === 'overview' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' }, gap: 2, pb: 1 }}>
          <Stack spacing={1.5}>
            <ResultPanel title="Topic context" rows={[
              ['Audience', String((brief.audience as any)?.name || (brief.audience as any)?.audienceName || form.audienceName)],
              ['Task', String((brief.task as any)?.type || (brief.task as any)?.taskType || form.taskType)],
              ['Pain / need', String((brief.task as any)?.painOrNeed || form.painOrNeed)],
              ['Offer', String((brief.productContext as any)?.offerPromise || form.offerPromise)],
              ['Proof', String((brief.productContext as any)?.proofSummary || form.proofSummary)],
            ]} />
            <StyledTextField label="Audience" value={form.audienceName} onChange={(value) => onFormChange({ ...form, audienceName: String(value) })} fullWidth />
            <StyledTextField label="Task" value={form.taskType} onChange={(value) => onFormChange({ ...form, taskType: String(value) })} fullWidth />
            <StyledTextField label="Offer promise" value={form.offerPromise} onChange={(value) => onFormChange({ ...form, offerPromise: String(value) })} fullWidth multiline minRows={2} />
            <StyledTextField label="Proof guardrails" value={form.proofSummary} onChange={(value) => onFormChange({ ...form, proofSummary: String(value) })} fullWidth multiline minRows={2} />
          </Stack>
          <Stack spacing={1.5}>
            <Typography variant="h6">Source context</Typography>
            <ResultPanel title="Selected sources" rows={[
              ['Source refs', String(sourcePaths.length)],
              ['Fallback sources', String(fallbackSourceIds.length)],
              ['Audience/task hydration', sourceHydratedData ? 'Loaded' : 'Not loaded'],
            ]} />
            <FallbackSourcePicker
              selectedIds={fallbackSourceIds}
              sources={fallbackSources}
              onToggle={onFallbackSourceToggle}
            />
          </Stack>
        </Box>
        )}
        {detailTab === 'research' && (
        <Stack spacing={1.5}>
          <StyledTextField label="Research instruction" value={form.researchInstruction} onChange={(value) => onFormChange({ ...form, researchInstruction: String(value) })} fullWidth multiline minRows={3} />
          <Button
            data-testid={AD_CREATIVE.selectedBriefResearch}
            variant="contained"
            startIcon={running ? <CircularProgress size={18} color="inherit" /> : <InsightsIcon />}
            disabled={running}
            onClick={onRunResearch}
          >
            Run research
          </Button>
          {researchRun ? (
            <ResultPanel title="Research result" rows={[
              ['Run', researchRun.id],
              ['Status', researchRun.status],
              ['Findings', formatList((researchRun.researchAnalysis as any)?.findings)],
            ]} />
          ) : <EmptyState title="Research not generated" detail="Run research for this selected campaign brief/topic." />}
        </Stack>
        )}
        {detailTab === 'angles' && (
        <Stack spacing={1.5}>
          <StyledTextField label="Angle instruction" value={form.angleInstruction} onChange={(value) => onFormChange({ ...form, angleInstruction: String(value) })} fullWidth multiline minRows={3} />
          <Button
            data-testid={AD_CREATIVE.selectedBriefAngles}
            variant="contained"
            startIcon={running ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={running}
            onClick={onGenerateAngles}
          >
            Generate angles
          </Button>
          {angles.length > 0 ? (
            <Stack data-testid={AD_CREATIVE.angleList} spacing={1}>
              {angles.map((angle) => (
                <Box key={String(angle.id)} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{String(angle.label || angle.id)}</Typography>
                  <Typography variant="caption" color="text.secondary">{String(angle.thesis || '')}</Typography>
                </Box>
              ))}
            </Stack>
          ) : <EmptyState title="No angles yet" detail="Generate angles after research or directly from this topic brief." />}
        </Stack>
        )}
        {detailTab === 'design' && (
        <ResultPanel title="Selected design context" rows={[
          ['Collection', selectedCollection?.name || brief.designCollectionId || 'Not selected'],
          ['Presets', selectedPresets.map((preset) => humanizeId(preset.creativeType)).join(', ') || 'None'],
          ['Templates', String(uniqueStrings(selectedPresets.flatMap((preset) => preset.templateIds ?? [])).length)],
        ]} />
        )}
        {detailTab === 'creatives' && (
        <Stack spacing={1.5}>
          <ResultPanel title="Creative generation context" rows={[
            ['Campaign assignment', campaign?.title || 'Not assigned'],
            ['Topic', brief.title],
            ['Design collection', selectedCollection?.name || brief.designCollectionId || 'Not selected'],
            ['Presets', String(selectedPresets.length)],
            ['Visual assets', generateAssets ? `Enabled (${imageModel})` : 'Plan/copy only'],
          ]} />
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Creative set generation</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Runs through backend tasks. The selected design collection supplies creative-type style and template context.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      data-testid={AD_CREATIVE.assetSwitch}
                      checked={generateAssets}
                      onChange={(event) => onGenerateAssetsChange(event.target.checked)}
                    />
                  }
                  label="Visual assets"
                />
              </Stack>
              {generateAssets && (
                <AvailableImageModelSelector
                  projectId={projectId}
                  value={imageModel}
                  onChange={onImageModelChange}
                  label="Image generator"
                  sx={{ width: '100%' }}
                />
              )}
              <Button
                data-testid={AD_CREATIVE.selectedBriefGenerateCreatives}
                variant="contained"
                startIcon={running ? <CircularProgress size={18} color="inherit" /> : <CollectionsBookmarkIcon />}
                disabled={running}
                onClick={onGenerateCreatives}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                Create creative set
              </Button>
            </Stack>
          </Box>
          <CreativeGrid items={items} onOpenItem={onOpenItem} />
        </Stack>
        )}
      </Box>
    </Box>
  );
}

function BriefTopicGenerationDialog({
  open,
  projectId,
  campaign,
  form,
  model,
  sourcePaths,
  sourceHydratedData,
  fallbackSourceIds,
  fallbackSources,
  selectedCollection,
  selectedPresets,
  running,
  onClose,
  onFormFieldChange,
  onModelChange,
  onSourcePathsChange,
  onHydratedSourceChange,
  onFallbackSourceToggle,
  onGenerate,
}: {
  open: boolean;
  projectId: string;
  campaign: AdCreativeCampaign | null;
  form: BriefForm;
  model: string;
  sourcePaths: string[];
  sourceHydratedData: HydratedData | null;
  fallbackSourceIds: string[];
  fallbackSources: SourceOption[];
  selectedCollection: AdCreativeDesignCollection | null;
  selectedPresets: AdCreativeDesignPreset[];
  running: boolean;
  onClose: () => void;
  onFormFieldChange: (field: keyof BriefForm, value: string) => void;
  onModelChange: (model: string) => void;
  onSourcePathsChange: (paths: string[]) => void;
  onHydratedSourceChange: (data: HydratedData) => void;
  onFallbackSourceToggle: (sourceId: string) => void;
  onGenerate: () => void;
}): React.ReactElement {
  const selectedFallbackSources = useMemo(
    () => fallbackSources.filter((source) => fallbackSourceIds.includes(source.id)),
    [fallbackSourceIds, fallbackSources],
  );
  const generationContext = useMemo(
    () => resolveBriefGenerationContext({
      form,
      sourcePaths,
      hydratedData: sourceHydratedData,
      fallbackSources: selectedFallbackSources,
    }),
    [form, sourceHydratedData, sourcePaths, selectedFallbackSources],
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth data-testid={AD_CREATIVE.generateBriefTopicsDialog}>
      <DialogTitle>Generate brief/topic candidates</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            This starts a backend task and saves generated candidates in generation history. The backlog changes only after you review and apply selected candidates.
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 420px' }, gap: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6">Topic generation setup</Typography>
              <AvailableModelSelector
                projectId={projectId}
                value={model}
                onChange={onModelChange}
                label="Brief/topic generator"
                sx={{ width: '100%' }}
              />
              <StyledTextField
                data-testid={AD_CREATIVE.generateBriefTopicsAudience}
                label="Audience"
                value={form.audienceName}
                onChange={(value) => onFormFieldChange('audienceName', String(value))}
                fullWidth
              />
              <StyledTextField
                data-testid={AD_CREATIVE.generateBriefTopicsTask}
                label="Task"
                value={form.taskType}
                onChange={(value) => onFormFieldChange('taskType', String(value))}
                fullWidth
              />
              <StyledTextField label="Pain or need" value={form.painOrNeed} onChange={(value) => onFormFieldChange('painOrNeed', String(value))} fullWidth multiline minRows={2} />
              <StyledTextField label="Offer promise" value={form.offerPromise} onChange={(value) => onFormFieldChange('offerPromise', String(value))} fullWidth multiline minRows={2} />
              <StyledTextField label="CTA" value={form.cta} onChange={(value) => onFormFieldChange('cta', String(value))} fullWidth />
              <StyledTextField label="Proof guardrails" value={form.proofSummary} onChange={(value) => onFormFieldChange('proofSummary', String(value))} fullWidth multiline minRows={2} />
              <StyledTextField label="Generation instruction" value={form.topicInstruction} onChange={(value) => onFormFieldChange('topicInstruction', String(value))} fullWidth multiline minRows={3} />
            </Stack>
            <Stack spacing={1.5}>
              <ResultPanel title="Selected generation context" rows={[
                ['Campaign assignment', campaign?.title || 'Not assigned'],
                ['Platform', campaign?.networkId || selectedCollection?.networkId || 'Selected platform'],
                ['Design collection', selectedCollection?.name || 'Not selected'],
                ['Presets', selectedPresets.map((preset) => humanizeId(preset.creativeType)).join(', ') || 'None'],
                ['Model', model],
                ['Selected sources', String(generationContext.selectedContextCount)],
              ]} />
              {!generationContext.canGenerate && (
                <Alert severity="info">
                  {generationContext.missingMessage}
                </Alert>
              )}
              <Typography variant="h6">Source data picker</Typography>
              <Box data-testid={AD_CREATIVE.sourcePicker} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <CompositeDataPicker
                  projectId={projectId}
                  savedSelections={sourcePaths}
                  onSelectionChange={onSourcePathsChange}
                  onHydratedDataChange={onHydratedSourceChange}
                  pickers={SOURCE_PICKERS}
                  layout="tabs"
                  height={320}
                  showInvalidPaths
                />
              </Box>
              <FallbackSourcePicker
                selectedIds={fallbackSourceIds}
                sources={fallbackSources}
                onToggle={onFallbackSourceToggle}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          data-testid={AD_CREATIVE.generateBriefTopicsSubmit}
          variant="contained"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          disabled={running || !generationContext.canGenerate}
          onClick={onGenerate}
        >
          Generate briefs/topics
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreativeSetsTab({
  campaign,
  brief,
  plan,
  selectedCollection,
  selectedPresets,
  selectedTemplates,
  items,
  generateAssets,
  running,
  onGenerateAssetsChange,
  onPlan,
  onGenerate,
  onOpenItem,
}: {
  campaign: AdCreativeCampaign | null;
  brief: AdCreativeBrief | null;
  plan: AdCreativePlan | null;
  selectedCollection: AdCreativeDesignCollection | null;
  selectedPresets: AdCreativeDesignPreset[];
  selectedTemplates: AdCreativeTemplate[];
  items: AdCreativeItem[];
  generateAssets: boolean;
  running: boolean;
  onGenerateAssetsChange: (value: boolean) => void;
  onPlan: () => void;
  onGenerate: () => void;
  onOpenItem: (item: AdCreativeItem) => void;
}): React.ReactElement {
  return (
    <Stack spacing={2}>
      {(!campaign || !brief) && <Alert severity="info">Select a campaign and build a campaign brief/topic before generation.</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="h6">Creative set generation</Typography>
          <Typography variant="body2" color="text.secondary">
            Generation runs through backend tasks. The brief supplies audience/task/proof; the selected platform collection supplies creative-type presets.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Switch data-testid={AD_CREATIVE.assetSwitch} checked={generateAssets} onChange={(event) => onGenerateAssetsChange(event.target.checked)} />}
            label="Visual assets"
          />
          <Button data-testid={AD_CREATIVE.plan} variant="outlined" disabled={!brief || running} onClick={onPlan}>
            Plan
          </Button>
          <Button
            data-testid={AD_CREATIVE.generate}
            variant="contained"
            startIcon={running ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={!campaign || !brief || running || selectedTemplates.length === 0}
            onClick={onGenerate}
          >
            Generate set
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '380px minmax(0, 1fr)' }, gap: 2 }}>
        <Stack spacing={1.5}>
          <ResultPanel title="Generation settings" rows={[
            ['Campaign', campaign?.title || 'Not selected'],
            ['Brief / Topic', brief?.title || 'Not created'],
            ['Design collection', selectedCollection?.name || 'Not selected'],
            ['Presets', selectedPresets.map((preset) => humanizeId(preset.creativeType)).join(', ') || 'None'],
            ['Templates', selectedTemplates.map((template) => template.name).join(', ') || 'None'],
            ['Plan rows', String(plan?.rows?.length ?? 0)],
          ]} />
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {Object.entries(selectedCollection?.styleAnchor ?? {}).map(([key, value]) => (
              <Chip key={key} size="small" variant="outlined" label={`${key}: ${Array.isArray(value) ? value.join(' ') : String(value)}`} />
            ))}
          </Stack>
        </Stack>

        <CreativeGrid items={items} onOpenItem={onOpenItem} />
      </Box>
    </Stack>
  );
}

function LaunchLearnTab({
  campaign,
  run,
  launchPlan,
  manifest,
  learningSnapshot,
  improvementRun,
  running,
  onCreateLaunch,
  onExport,
  onImportMetrics,
  onImprove,
}: {
  campaign: AdCreativeCampaign | null;
  run: AdCreativeGenerationRun | null;
  launchPlan: Record<string, unknown> | null;
  manifest: Record<string, unknown> | null;
  learningSnapshot: Record<string, unknown> | null;
  improvementRun: Record<string, unknown> | null;
  running: boolean;
  onCreateLaunch: () => void;
  onExport: () => void;
  onImportMetrics: () => void;
  onImprove: () => void;
}): React.ReactElement {
  return (
    <Stack spacing={2}>
      {(!campaign || !run) && <Alert severity="info">Generate a creative set before creating launch drafts.</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
        <ActionStep
          title="1. Launch plan"
          detail={launchPlan ? String(launchPlan.id) : 'Create campaign, ad set, ad, experiment, and tracking drafts.'}
          buttonLabel="Create"
          disabled={!campaign || !run || running}
          done={Boolean(launchPlan)}
          onClick={onCreateLaunch}
          testId={AD_CREATIVE.createLaunchPlan}
        />
        <ActionStep
          title="2. Export"
          detail={manifest ? String(manifest.status || 'ready') : 'Build manual CSV and setup manifest for the ad platform.'}
          buttonLabel="Export"
          disabled={!launchPlan || running}
          done={Boolean(manifest)}
          onClick={onExport}
          testId={AD_CREATIVE.exportLaunchPlan}
        />
        <ActionStep
          title="3. Metrics"
          detail={learningSnapshot
            ? 'Descriptive evidence snapshot imported'
            : 'Requires explicit reconciled analytics rows. Synthetic sample metrics are disabled.'}
          buttonLabel="Evidence required"
          disabled
          done={Boolean(learningSnapshot)}
          onClick={onImportMetrics}
          testId={AD_CREATIVE.importMetrics}
        />
        <ActionStep
          title="4. Improve"
          detail={improvementRun ? String(improvementRun.status || 'proposal_ready') : 'Create improvement proposal from winners and losers.'}
          buttonLabel="Improve"
          disabled={!learningSnapshot || running}
          done={Boolean(improvementRun)}
          onClick={onImprove}
          testId={AD_CREATIVE.improveCampaign}
        />
      </Box>

      <ResultPanel title="Launch plan" rows={[
        ['Campaign', campaign?.title || 'Not selected'],
        ['Generation run', run?.id || 'Not generated'],
        ['Launch plan', launchPlan ? String(launchPlan.id) : 'Not created'],
        ['Manifest', manifest ? String(manifest.id) : 'Not exported'],
        ['Learning snapshot', learningSnapshot ? String(learningSnapshot.id) : 'Not imported'],
        ['Improvement run', improvementRun ? String(improvementRun.id) : 'Not created'],
      ]} />
    </Stack>
  );
}

function HistoryTab({
  briefs,
  items,
  lastRun,
}: {
  briefs: AdCreativeBrief[];
  items: AdCreativeItem[];
  lastRun: AdCreativeGenerationRun | null;
}): React.ReactElement {
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6">Workspace history</Typography>
      <ResultPanel title="Latest state" rows={[
        ['Briefs', String(briefs.length)],
        ['Creatives', String(items.length)],
        ['Last run', lastRun?.id || 'Not generated'],
        ['Last run status', lastRun?.status || 'No run'],
      ]} />
      <Stack spacing={1}>
        {items.slice(0, 8).map((item) => (
          <Box key={item.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {creativeLocaleHeadline(item, item.defaultLanguage, 'strict')}
              </Typography>
              <Chip size="small" label={item.status} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {humanizeId(item.creativeType)} - {item.designCollectionId || 'collection'}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

function FallbackSourcePicker({
  selectedIds,
  sources,
  onToggle,
}: {
  selectedIds: string[];
  sources: SourceOption[];
  onToggle: (sourceId: string) => void;
}): React.ReactElement {
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Example sources
      </Typography>
      <Stack spacing={1}>
        {sources.map((source) => (
          <ButtonBase
            key={source.id}
            data-testid={AD_CREATIVE.sourceOption(source.id)}
            onClick={() => onToggle(source.id)}
            sx={{
              border: 1,
              borderColor: selectedIds.includes(source.id) ? 'primary.main' : 'divider',
              borderRadius: 1,
              display: 'block',
              p: 1,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox checked={selectedIds.includes(source.id)} size="small" sx={{ p: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{source.label}</Typography>
                  <Chip size="small" variant="outlined" label={source.type} />
                </Stack>
                <Typography variant="caption" color="text.secondary">{source.summary}</Typography>
              </Box>
            </Stack>
          </ButtonBase>
        ))}
      </Stack>
    </Stack>
  );
}

function SelectableChips({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}): React.ReactElement {
  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap">
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <Chip
              key={option}
              label={option}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => onChange(active ? selected.filter((entry) => entry !== option) : [...selected, option])}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

function ActionStep({
  title,
  detail,
  buttonLabel,
  disabled,
  done,
  onClick,
  testId,
}: {
  title: string;
  detail: string;
  buttonLabel: string;
  disabled: boolean;
  done: boolean;
  onClick: () => void;
  testId: string;
}): React.ReactElement {
  return (
    <Box sx={{ border: 1, borderColor: done ? 'success.light' : 'divider', borderRadius: 1, p: 1.5, minHeight: 180 }}>
      <Stack spacing={1} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
          <Chip size="small" color={done ? 'success' : 'default'} label={done ? 'Done' : 'Pending'} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{detail}</Typography>
        <Button data-testid={testId} variant={done ? 'outlined' : 'contained'} disabled={disabled} onClick={onClick}>
          {buttonLabel}
        </Button>
      </Stack>
    </Box>
  );
}

function CreativeGrid({ items, onOpenItem }: { items: AdCreativeItem[]; onOpenItem: (item: AdCreativeItem) => void }): React.ReactElement {
  return (
    <Box
      data-testid={AD_CREATIVE.list}
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}
    >
      {items.length === 0 && (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <EmptyState title="No creatives generated" detail="Plan and generate a creative set to review concrete ads by platform preset." />
        </Box>
      )}
      {items.map((item) => {
        const projection = resolveCreativeLocaleProjection(item, item.defaultLanguage, 'strict');
        const copy = getExactCreativeLocaleCopy(item, item.defaultLanguage);
        return (
          <Box key={item.id} data-testid={AD_CREATIVE.item(item.id)} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {projection.previewUrl ? (
              projection.previewMediaKind === 'video' ? (
                <Box component="video" data-testid={AD_CREATIVE.image(item.id)} src={projection.previewUrl} muted preload="metadata" sx={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', bgcolor: 'grey.100' }} />
              ) : (
                <Box
                  component="img"
                  data-testid={AD_CREATIVE.image(item.id)}
                  src={projection.previewUrl}
                  alt={item.templateId || item.creativeType}
                  sx={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', bgcolor: 'grey.100' }}
                />
              )
            ) : (
              <Box sx={{ width: '100%', aspectRatio: '4 / 3', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Prompt planned</Typography>
              </Box>
            )}
            <Stack spacing={1} sx={{ p: 1.25 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                <Chip size="small" label={item.status} />
                <Chip size="small" variant="outlined" label={humanizeId(item.creativeType)} />
                <Chip size="small" variant="outlined" label={item.designPresetId || item.templateId} />
              </Stack>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{copy.headline ?? item.templateId}</Typography>
              <Typography variant="body2" color="text.secondary">{copy.primaryText}</Typography>
              <Button data-testid={AD_CREATIVE.open(item.id)} size="small" variant="contained" startIcon={<OpenInNewIcon />} onClick={() => onOpenItem(item)}>
                Open
              </Button>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function ResultPanel({ title, rows, testId }: { title: string; rows: Array<[string, string]>; testId?: string }): React.ReactElement {
  return (
    <Box data-testid={testId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      <Stack spacing={0.75}>
        {rows.map(([label, value]) => (
          <Stack key={label} direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="caption" sx={{ textAlign: 'right', maxWidth: '70%', overflowWrap: 'anywhere' }}>{value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }): React.ReactElement {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Box>
  );
}

function DesignMockup({ collection }: { collection: AdCreativeDesignCollection | null }): React.ReactElement {
  const palette = collection?.styleAnchor?.palette;
  const colors = Array.isArray(palette)
    ? palette as string[]
    : ['#111827', '#F59E0B', '#FFFFFF'];
  return (
    <Box sx={{ aspectRatio: '16 / 9', borderRadius: 1, overflow: 'hidden', bgcolor: colors[0], display: 'grid', gridTemplateColumns: '1.2fr 0.8fr' }}>
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors[2] || '#FFFFFF', fontWeight: 700 }} noWrap>{collection?.name ?? 'Design collection'}</Typography>
        <Box>
          <Box sx={{ width: '68%', height: 8, bgcolor: colors[2] || '#FFFFFF', borderRadius: 0.5, mb: 0.75 }} />
          <Box sx={{ width: '44%', height: 18, bgcolor: colors[1] || '#F59E0B', borderRadius: 0.75 }} />
        </Box>
      </Box>
      <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: '76%', aspectRatio: '1 / 1', border: `1px solid ${colors[2] || '#FFFFFF'}`, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.12)' }} />
      </Box>
    </Box>
  );
}

function CreativeDetailDialog({
  projectId,
  client,
  item,
  onItemUpdated,
  onClose,
}: {
  projectId: string;
  client: AdCreativeApiClient;
  item: AdCreativeItem | null;
  onItemUpdated: (item: AdCreativeItem) => void;
  onClose: () => void;
}): React.ReactElement | null {
  const experiment = item ? getFirstExperiment(item) : undefined;
  const [selectedCreativeLocale, setSelectedCreativeLocale] = useState('');
  const [metaStatus, setMetaStatus] = useState<MetaAdsConnectionStatus | null>(null);
  const [metaAccounts, setMetaAccounts] = useState<MetaAdsAdAccountSummary[]>([]);
  const [metaPages, setMetaPages] = useState<MetaAdsPageSummary[]>([]);
  const [metaCampaigns, setMetaCampaigns] = useState<MetaAdsCampaignSummary[]>([]);
  const [metaAdSets, setMetaAdSets] = useState<MetaAdsAdSetSummary[]>([]);
  const [selectedMetaAccountId, setSelectedMetaAccountId] = useState('');
  const [selectedMetaPageId, setSelectedMetaPageId] = useState('');
  const [selectedMetaCampaignId, setSelectedMetaCampaignId] = useState('');
  const [selectedMetaAdSetId, setSelectedMetaAdSetId] = useState('');
  const [metaFallbackApproved, setMetaFallbackApproved] = useState(false);
  const [metaDraft, setMetaDraft] = useState<AdCreativeMetaDraft | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaActionLoading, setMetaActionLoading] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [metaSuccess, setMetaSuccess] = useState<string | null>(null);
  const exactLocale = item
    ? findCreativeLocaleKey(item, selectedCreativeLocale) ?? item.defaultLanguage
    : '';
  const localeProjection = item && exactLocale
    ? resolveCreativeLocaleProjection(item, exactLocale, 'strict')
    : null;
  const localizedCopy = item && exactLocale ? getExactCreativeLocaleCopy(item, exactLocale) : {};
  const destinationUrl = item && exactLocale ? getAdCreativeDestinationUrl(item, exactLocale) : '';
  const connectedToMeta = Boolean(metaStatus?.connected && !metaStatus.needsReconnect);

  useEffect(() => {
    if (!item) {
      setSelectedCreativeLocale('');
      return;
    }
    setSelectedCreativeLocale((current) => findCreativeLocaleKey(item, current) ?? item.defaultLanguage);
  }, [item?.id, item?.localeRevision]);

  useEffect(() => {
    if (!item) return;
    setMetaFallbackApproved(false);
    setSelectedMetaAccountId(item.providerAccountId || '');
    setSelectedMetaCampaignId(item.providerCampaignId || '');
    setSelectedMetaAdSetId(item.providerAdSetId || '');
    setMetaDraft(getAdCreativeExistingMetaDraft(item));
    setMetaError(null);
    setMetaSuccess(null);
  }, [destinationUrl, item]);

  const loadMetaBasics = React.useCallback(async () => {
    if (!projectId || !item) return;
    setMetaLoading(true);
    setMetaError(null);
    try {
      const status = await client.getMetaAdsStatus(projectId);
      if (status.error) throw new Error(status.error);
      setMetaStatus(status);
      if (!status.connected || status.needsReconnect) {
        setMetaAccounts([]);
        setMetaPages([]);
        setMetaCampaigns([]);
        setMetaAdSets([]);
        setSelectedMetaAccountId('');
        setSelectedMetaPageId('');
        setSelectedMetaCampaignId('');
        setSelectedMetaAdSetId('');
        return;
      }

      const [accountsResponse, pagesResponse] = await Promise.all([
        client.listMetaAdAccounts(projectId),
        client.listMetaPages(projectId),
      ]);
      if (accountsResponse.error) throw new Error(accountsResponse.error);
      if (pagesResponse.error) throw new Error(pagesResponse.error);
      const accounts = mergeMetaAdAccounts(
        accountsResponse.accounts || [],
        status.accessibleAdAccounts || [],
      );
      const pages = pagesResponse.pages || [];
      setMetaAccounts(accounts);
      setMetaPages(pages);
      setSelectedMetaAccountId((current) => {
        const currentAccount = findMetaAdAccount(accounts, current);
        if (currentAccount) return currentAccount.id;
        const itemAccount = findMetaAdAccount(accounts, item.providerAccountId);
        if (itemAccount) return itemAccount.id;
        return accounts[0]?.id || '';
      });
      setSelectedMetaPageId((current) => {
        if (current && pages.some((page) => page.id === current)) return current;
        return pages[0]?.id || '';
      });
    } catch (error: any) {
      setMetaError(error?.message || 'Failed to load Meta connection');
    } finally {
      setMetaLoading(false);
    }
  }, [client, item, projectId]);

  useEffect(() => {
    void loadMetaBasics();
  }, [loadMetaBasics]);

  useEffect(() => {
    if (!projectId || !connectedToMeta || !selectedMetaAccountId) {
      setMetaCampaigns([]);
      setSelectedMetaCampaignId('');
      setMetaAdSets([]);
      setSelectedMetaAdSetId('');
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void client.listMetaCampaigns(projectId, selectedMetaAccountId)
      .then((response) => {
        if (cancelled) return;
        if (response.error) throw new Error(response.error);
        const campaigns = response.campaigns || [];
        setMetaCampaigns(campaigns);
        setSelectedMetaCampaignId((current) => {
          if (current && campaigns.some((campaign) => campaign.id === current)) return current;
          if (item?.providerCampaignId && campaigns.some((campaign) => campaign.id === item.providerCampaignId)) {
            return item.providerCampaignId;
          }
          return campaigns[0]?.id || '';
        });
      })
      .catch((error: any) => {
        if (!cancelled) setMetaError(error?.message || 'Failed to load Meta campaigns');
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, connectedToMeta, item?.providerCampaignId, projectId, selectedMetaAccountId]);

  useEffect(() => {
    if (!projectId || !connectedToMeta || !selectedMetaAccountId || !selectedMetaCampaignId) {
      setMetaAdSets([]);
      setSelectedMetaAdSetId('');
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void client.listMetaAdSets(projectId, selectedMetaAccountId, selectedMetaCampaignId)
      .then((response) => {
        if (cancelled) return;
        if (response.error) throw new Error(response.error);
        const adsets = response.adsets || [];
        setMetaAdSets(adsets);
        setSelectedMetaAdSetId((current) => {
          if (current && adsets.some((adset) => adset.id === current)) return current;
          if (item?.providerAdSetId && adsets.some((adset) => adset.id === item.providerAdSetId)) {
            return item.providerAdSetId;
          }
          return adsets[0]?.id || '';
        });
      })
      .catch((error: any) => {
        if (!cancelled) setMetaError(error?.message || 'Failed to load Meta ad sets');
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, connectedToMeta, item?.providerAdSetId, projectId, selectedMetaAccountId, selectedMetaCampaignId]);

  const connectMeta = async () => {
    if (!projectId) return;
    setMetaActionLoading(true);
    setMetaError(null);
    try {
      const response = await client.getMetaAdsOAuthUrl(projectId, getCurrentRelativeUrl());
      if (response.error || !response.url) throw new Error(response.error || 'OAuth URL was not returned');
      window.location.href = response.url;
    } catch (error: any) {
      setMetaError(error?.message || 'Failed to start Meta OAuth');
      setMetaActionLoading(false);
    }
  };

  const disconnectMeta = async () => {
    if (!projectId) return;
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Disconnect Meta Ads for this project? You can reconnect before creating another paused ad draft.');
      if (!confirmed) return;
    }
    setMetaActionLoading(true);
    setMetaError(null);
    setMetaSuccess(null);
    try {
      const status = await client.disconnectMetaAds(projectId);
      if (status.error) throw new Error(status.error);
      setMetaStatus(status);
      setMetaAccounts([]);
      setMetaPages([]);
      setMetaCampaigns([]);
      setMetaAdSets([]);
      setSelectedMetaAccountId('');
      setSelectedMetaPageId('');
      setSelectedMetaCampaignId('');
      setSelectedMetaAdSetId('');
      setMetaSuccess('Meta Ads disconnected');
    } catch (error: any) {
      setMetaError(error?.message || 'Failed to disconnect Meta Ads');
    } finally {
      setMetaActionLoading(false);
    }
  };

  const createMetaDraft = async () => {
    if (!item) return;
    if (item.review?.approvalStage !== 'approved') {
      setMetaError('Approve this Segmently creative before creating any Meta object.');
      return;
    }
    setMetaActionLoading(true);
    setMetaError(null);
    setMetaSuccess(null);
    try {
      const response = await client.createMetaDraft(projectId, item.id, {
        locale: exactLocale,
        adAccountId: selectedMetaAccountId,
        pageId: selectedMetaPageId,
        campaignId: selectedMetaCampaignId,
        adSetId: selectedMetaAdSetId,
        fallbackApproved: metaFallbackApproved,
        name: localizedCopy.headline ?? item.templateId ?? item.id,
      });
      if (!response.success || !response.data?.draft || !response.data.item) {
        throw new Error(response.error || 'Failed to create Meta paused ad draft');
      }
      setMetaDraft(response.data.draft);
      onItemUpdated(response.data.item);
      setMetaSuccess('Paused ad draft created in Meta');
    } catch (error: any) {
      setMetaError(error?.message || 'Failed to create Meta paused ad draft');
    } finally {
      setMetaActionLoading(false);
    }
  };

  const checkMetaDraftStatus = async () => {
    if (!metaDraft) return;
    setMetaActionLoading(true);
    setMetaError(null);
    try {
      const response = await client.checkMetaDraftStatus(projectId, metaDraft.id);
      if (!response.success || !response.data?.draft) {
        throw new Error(response.error || 'Failed to check Meta ad draft status');
      }
      setMetaDraft(response.data.draft);
      setMetaSuccess('Meta ad draft status refreshed');
    } catch (error: any) {
      setMetaError(error?.message || 'Failed to check Meta ad draft status');
    } finally {
      setMetaActionLoading(false);
    }
  };

  if (!item) return null;
  const metaAccountOptions = buildMetaAccountOptions(metaAccounts, selectedMetaAccountId);
  const selectedMetaAccountExists = Boolean(selectedMetaAccountId)
    && Boolean(findMetaAdAccount(metaAccountOptions, selectedMetaAccountId));
  const selectedMetaPageExists = Boolean(selectedMetaPageId)
    && metaPages.some((page) => page.id === selectedMetaPageId);
  const selectedMetaCampaignExists = Boolean(selectedMetaCampaignId)
    && metaCampaigns.some((campaign) => campaign.id === selectedMetaCampaignId);
  const selectedMetaAdSetExists = Boolean(selectedMetaAdSetId)
    && metaAdSets.some((adset) => adset.id === selectedMetaAdSetId);
  const localeStatus = item ? getCreativeLocaleStatus(item, exactLocale) : 'missing';
  const localeReady = localeStatus === 'source' || localeStatus === 'ready';
  const itemApproved = item.review?.approvalStage === 'approved';
  const canCreateMetaDraft = connectedToMeta
    && itemApproved
    && localeReady
    && Boolean(localeProjection?.copy.headline.hasRequestedValue)
    && Boolean(localeProjection?.copy.primaryText.hasRequestedValue)
    && Boolean(localeProjection?.previewUrl)
    && Boolean(item && Object.prototype.hasOwnProperty.call(item.localizedDestinations ?? {}, exactLocale))
    && selectedMetaAccountExists
    && selectedMetaPageExists
    && selectedMetaCampaignExists
    && selectedMetaAdSetExists
    && Boolean(destinationUrl);
  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="md" fullWidth data-testid={AD_CREATIVE.detailDialog}>
      <DialogTitle>{localizedCopy.headline ?? item.templateId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <StyledTextField
            select
            label="Creative language"
            size="small"
            value={exactLocale}
            onChange={(value) => setSelectedCreativeLocale(String(value))}
            fullWidth
          >
            {item.languages.map((locale) => (
              <MenuItem key={locale} value={locale}>{locale} · {getCreativeLocaleStatus(item, locale).replace(/_/g, ' ')}</MenuItem>
            ))}
          </StyledTextField>
          {localeProjection?.previewUrl ? (
            localeProjection.previewMediaKind === 'video' ? (
              <Box component="video" controls muted data-testid={AD_CREATIVE.detailImage} src={localeProjection.previewUrl} sx={{ width: '100%', maxHeight: 420, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }} />
            ) : (
              <Box component="img" data-testid={AD_CREATIVE.detailImage} src={localeProjection.previewUrl} alt={item.templateId || item.id} sx={{ width: '100%', maxHeight: 420, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }} />
            )
          ) : (
            <Box data-testid={AD_CREATIVE.detailImage} sx={{ width: '100%', minHeight: 220, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">Prompt planned</Typography>
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <ResultPanel testId={AD_CREATIVE.detailCopy} title="Copy" rows={[
              ['Headline', localizedCopy.headline ?? ''],
              ['Primary text', localizedCopy.primaryText ?? ''],
              ['Description', localizedCopy.description ?? ''],
              ['CTA', localizedCopy.cta ?? ''],
            ]} />
            <ResultPanel testId={AD_CREATIVE.detailSettings} title="Settings" rows={[
              ['Creative type', humanizeId(item.creativeType)],
              ['Template', item.templateId || ''],
              ['Design collection', item.designCollectionId || ''],
              ['Design preset', item.designPresetId || ''],
              ['Network', item.networkId || ''],
              ['Campaign', item.campaignId || ''],
              ['Plan', item.planId || ''],
              ['Run', item.generationRunId || ''],
              ['Destination', destinationUrl],
            ]} />
          </Box>
          {experiment?.promptTemplate && (
            <Box data-testid={AD_CREATIVE.detailAssetPrompt} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Asset prompt</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{experiment.promptTemplate}</Typography>
            </Box>
          )}
          {isVideoCreativeItem(item) && (
            <AdCreativeVideoSection
              projectId={projectId}
              item={item}
              client={client}
              onRefreshItem={async () => {
                const response = await client.listItems(projectId, item.briefId ? { briefId: item.briefId } : {});
                const fresh = response.data?.items?.find((candidate) => candidate.id === item.id);
                if (fresh) onItemUpdated(fresh);
              }}
            />
          )}
          <Box data-testid={AD_CREATIVE.metaDraftPanel} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Meta paused ad draft</Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={connectedToMeta ? 'success' : 'default'} label={connectedToMeta ? 'Connected' : 'Not connected'} />
                    {metaStatus?.displayName && <Chip size="small" label={`Connected as ${metaStatus.displayName}`} />}
                    {metaStatus?.scopes?.map((scope) => <Chip key={scope} size="small" variant="outlined" label={scope} />)}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    data-testid={AD_CREATIVE.metaDraftRefresh}
                    variant="outlined"
                    size="small"
                    startIcon={metaLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                    onClick={() => void loadMetaBasics()}
                    disabled={metaLoading || metaActionLoading}
                  >
                    Refresh
                  </Button>
                  {connectedToMeta && (
                    <Button
                      data-testid={AD_CREATIVE.metaDraftDisconnect}
                      variant="outlined"
                      size="small"
                      color="inherit"
                      onClick={() => void disconnectMeta()}
                      disabled={metaActionLoading}
                    >
                      Disconnect
                    </Button>
                  )}
                  {!connectedToMeta && (
                    <Button
                      data-testid={AD_CREATIVE.metaDraftConnect}
                      variant="contained"
                      size="small"
                      onClick={() => void connectMeta()}
                      disabled={metaActionLoading}
                    >
                      Connect Meta
                    </Button>
                  )}
                </Stack>
              </Stack>

              {metaError && <Alert severity="error">{metaError}</Alert>}
              {metaSuccess && <Alert severity="success">{metaSuccess}</Alert>}
              {!localeReady && (
                <Alert severity="warning">
                  {`${exactLocale} is ${localeStatus.replace(/_/g, ' ')}. Approve its exact copy, media, and destination before creating a Meta draft.`}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                <StyledTextField
                  select
                  label="Ad account"
                  value={selectedMetaAccountId}
                  data-testid={AD_CREATIVE.metaDraftAdAccount}
                  onChange={(value) => {
                    setSelectedMetaAccountId(String(value));
                    setSelectedMetaCampaignId('');
                    setSelectedMetaAdSetId('');
                  }}
                  disabled={!connectedToMeta || metaActionLoading}
                  fullWidth
                  size="small"
                >
                  {metaAccountOptions.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {formatMetaAdAccountLabel(account)}
                    </MenuItem>
                  ))}
                </StyledTextField>
                <StyledTextField
                  select
                  label="Page identity"
                  value={selectedMetaPageId}
                  data-testid={AD_CREATIVE.metaDraftPage}
                  onChange={(value) => setSelectedMetaPageId(String(value))}
                  disabled={!connectedToMeta || metaActionLoading}
                  fullWidth
                  size="small"
                >
                  {metaPages.map((page) => (
                    <MenuItem key={page.id} value={page.id}>
                      {page.name || page.id}
                    </MenuItem>
                  ))}
                </StyledTextField>
                <StyledTextField
                  select
                  label="Existing campaign"
                  value={selectedMetaCampaignId}
                  data-testid={AD_CREATIVE.metaDraftCampaign}
                  onChange={(value) => {
                    setSelectedMetaCampaignId(String(value));
                    setSelectedMetaAdSetId('');
                  }}
                  disabled={!connectedToMeta || metaActionLoading || !selectedMetaAccountId}
                  fullWidth
                  size="small"
                >
                  {metaCampaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name || campaign.id}
                    </MenuItem>
                  ))}
                </StyledTextField>
                <StyledTextField
                  select
                  label="Existing ad set"
                  value={selectedMetaAdSetId}
                  data-testid={AD_CREATIVE.metaDraftAdSet}
                  onChange={(value) => setSelectedMetaAdSetId(String(value))}
                  disabled={!connectedToMeta || metaActionLoading || !selectedMetaAccountId || !selectedMetaCampaignId}
                  fullWidth
                  size="small"
                >
                  {metaAdSets.map((adset) => (
                    <MenuItem key={adset.id} value={adset.id}>
                      {adset.name || adset.id}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </Box>
              <StyledTextField
                label="Exact localized destination"
                value={destinationUrl}
                data-testid={AD_CREATIVE.metaDraftLinkUrl}
                onChange={() => undefined}
                disabled
                fullWidth
                size="small"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={metaFallbackApproved}
                    onChange={(event) => setMetaFallbackApproved(event.target.checked)}
                    disabled={metaActionLoading}
                  />
                )}
                label="Allow a verified source-language destination fallback for this deployment"
              />
              {!itemApproved && (
                <Alert severity="info">
                  Approve this Segmently creative before creating a paused Meta ad draft.
                </Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  data-testid={AD_CREATIVE.metaDraftCreate}
                  variant="contained"
                  startIcon={metaActionLoading ? <CircularProgress size={16} /> : <SendIcon />}
                  disabled={!canCreateMetaDraft || metaActionLoading}
                  onClick={() => void createMetaDraft()}
                >
                  Create paused ad draft
                </Button>
                <Button
                  data-testid={AD_CREATIVE.metaDraftCheckStatus}
                  variant="outlined"
                  disabled={!metaDraft?.id || metaActionLoading}
                  onClick={() => void checkMetaDraftStatus()}
                >
                  Check status
                </Button>
              </Stack>
              {metaDraft && (
                <ResultPanel testId={AD_CREATIVE.metaDraftResult} title="Meta draft result" rows={[
                  ['Draft', metaDraft.id],
                  ['Ad', metaDraft.adId],
                  ['Creative', metaDraft.creativeId],
                  ['Status', metaDraft.status || ''],
                  ['Effective status', metaDraft.effectiveStatus || ''],
                ]} />
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid={AD_CREATIVE.detailClose} onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function getCurrentRelativeUrl(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getAdCreativeDestinationUrl(item: AdCreativeItem, locale: string): string {
  const destination = item.localizedDestinations?.[locale] as Record<string, unknown> | undefined;
  return typeof destination?.url === 'string' ? destination.url : '';
}

function getAdCreativeExistingMetaDraft(item: AdCreativeItem): AdCreativeMetaDraft | null {
  const exportState = item._export as Record<string, unknown> | undefined;
  const requiredExportString = (key: string): string => (
    typeof exportState?.[key] === 'string' ? String(exportState[key]).trim() : ''
  );
  const draftId = requiredExportString('metaDraftId');
  const locale = requiredExportString('metaDraftLocale');
  const pageId = requiredExportString('metaDraftPageId');
  const imageHash = requiredExportString('metaDraftImageHash');
  const renditionId = requiredExportString('metaDraftRenditionId');
  const exportManifestId = requiredExportString('metaDraftExportManifestId');
  const exportedManifestHash = requiredExportString('metaDraftExportedManifestHash');
  const providerPayloadHash = requiredExportString('metaDraftProviderPayloadHash');
  const providerPayloadVersion = requiredExportString('metaDraftProviderPayloadVersion');
  const deploymentLocaleBindingId = requiredExportString('metaDraftDeploymentLocaleBindingId');
  const deploymentKey = requiredExportString('metaDraftDeploymentKey');
  if (!draftId || !locale || !pageId || !imageHash || !renditionId || !exportManifestId
    || !exportedManifestHash || !providerPayloadHash || !providerPayloadVersion
    || !deploymentLocaleBindingId || !deploymentKey || !item.providerAdId || !item.providerCreativeId) {
    // No legacy synthesis: only the complete v3 Meta draft projection is
    // rendered as an existing Segmently deployment.
    return null;
  }
  return {
    id: draftId,
    itemId: item.id,
    locale,
    adAccountId: item.providerAccountId || '',
    pageId,
    campaignId: item.providerCampaignId || '',
    adSetId: item.providerAdSetId || '',
    imageHash,
    creativeId: item.providerCreativeId,
    adId: item.providerAdId,
    exportManifestId,
    renditionId,
    localizationVariantId: requiredExportString('metaDraftLocalizationVariantId') || undefined,
    exportedManifestHash,
    providerPayloadHash,
    providerPayloadVersion,
    deploymentLocaleBindingId,
    deploymentKey,
    status: typeof exportState?.metaDraftStatus === 'string' ? exportState.metaDraftStatus : undefined,
  };
}

function normalizeMetaAdAccountId(value: string | undefined): string {
  return (value || '').trim().replace(/^act_/i, '').toLowerCase();
}

function findMetaAdAccount(
  accounts: MetaAdsAdAccountSummary[],
  value: string | undefined,
): MetaAdsAdAccountSummary | undefined {
  const normalized = normalizeMetaAdAccountId(value);
  if (!normalized) return undefined;
  return accounts.find((account) => {
    return normalizeMetaAdAccountId(account.id) === normalized
      || normalizeMetaAdAccountId(account.accountId) === normalized;
  });
}

function mergeMetaAdAccounts(
  primary: MetaAdsAdAccountSummary[],
  fallback: MetaAdsAdAccountSummary[],
): MetaAdsAdAccountSummary[] {
  const merged: MetaAdsAdAccountSummary[] = [];
  for (const account of [...primary, ...fallback]) {
    if (!account?.id) continue;
    if (findMetaAdAccount(merged, account.id) || findMetaAdAccount(merged, account.accountId)) continue;
    merged.push(account);
  }
  return merged;
}

function buildMetaAccountOptions(
  accounts: MetaAdsAdAccountSummary[],
  selectedAccountId: string,
): MetaAdsAdAccountSummary[] {
  if (!selectedAccountId || findMetaAdAccount(accounts, selectedAccountId)) return accounts;
  return [
    {
      id: selectedAccountId,
      accountId: normalizeMetaAdAccountId(selectedAccountId) || undefined,
      name: `Ad account ${selectedAccountId}`,
    },
    ...accounts,
  ];
}

function formatMetaAdAccountLabel(account: MetaAdsAdAccountSummary): string {
  const id = account.accountId || normalizeMetaAdAccountId(account.id) || account.id;
  return account.name ? `${account.name} (${id})` : account.id;
}

interface BriefGenerationContext {
  audienceName: string;
  taskType: string;
  taskSummary: string;
  selectedContextCount: number;
  canGenerate: boolean;
  missingMessage: string;
}

function resolveBriefGenerationContext({
  form,
  sourcePaths,
  hydratedData,
  fallbackSources,
}: {
  form: BriefForm;
  sourcePaths: string[];
  hydratedData: HydratedData | null;
  fallbackSources: SourceOption[];
}): BriefGenerationContext {
  const ids = extractIdsFromPaths(sourcePaths);
  const audienceLabel = firstNonEmptyString([
    form.audienceName,
    ...(hydratedData?.audiences ?? []).map((audience) => entityText(audience, ['name', 'description', 'id'])),
    ...fallbackSources.filter((source) => source.type === 'audience').map((source) => source.label),
    ...ids.audienceIds.map((id) => `Selected audience ${id}`),
  ]);
  const taskLabel = firstNonEmptyString([
    form.taskType,
    ...(hydratedData?.tasks ?? []).map((task) => entityText(task, ['name', 'description', 'activationAction', 'id'])),
    ...fallbackSources.filter((source) => source.type === 'task').map((source) => source.label),
    ...ids.taskIds.map((id) => `Selected task ${id}`),
  ]);
  const taskSummary = firstNonEmptyString([
    form.painOrNeed,
    ...(hydratedData?.tasks ?? []).map((task) => entityText(task, ['description', 'activationAction', 'name'])),
    ...fallbackSources.filter((source) => source.type === 'task').map((source) => source.summary),
  ]);
  const selectedContextCount = sourcePaths.length + fallbackSources.length;
  const hasAudience = Boolean(audienceLabel);
  const hasTask = Boolean(taskLabel);
  const hasSourceContext = selectedContextCount > 0;
  const missing: string[] = [];
  if (!hasAudience) missing.push('an audience');
  if (!hasTask) missing.push('a task');
  if (!hasSourceContext) missing.push('at least one audience/task/insight/proof source');

  return {
    audienceName: audienceLabel || 'Selected audience',
    taskType: taskLabel || 'selected task',
    taskSummary,
    selectedContextCount,
    canGenerate: hasAudience && hasTask && hasSourceContext,
    missingMessage: missing.length > 0
      ? `Choose ${formatHumanList(missing)} before generation. Data picker selections count; manual fields are optional overrides.`
      : '',
  };
}

function entityText(entity: unknown, fields: string[]): string {
  if (!entity || typeof entity !== 'object') return '';
  const record = entity as Record<string, unknown>;
  for (const field of fields) {
    const value = record[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function firstNonEmptyString(values: string[]): string {
  return values.find((value) => typeof value === 'string' && value.trim())?.trim() ?? '';
}

function formatHumanList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function buildSourceRefs(
  sourcePaths: string[],
  hydratedData: HydratedData | null,
  fallbackSources: SourceOption[],
): Record<string, unknown> {
  const ids = sourcePaths.length > 0 ? extractIdsFromPaths(sourcePaths) : extractIdsFromFallback(fallbackSources);
  return {
    audienceIds: ids.audienceIds,
    taskIds: ids.taskIds,
    insightIds: ids.insightIds,
    reviewIds: ids.reviewIds,
    ratingIds: ids.ratingIds,
    studyCaseIds: ids.studyCaseIds,
    bigNumberIds: ids.bigNumberIds,
    customRefs: [
      ...sourcePaths.map((path) => ({
        sourceType: extractSourceType(path),
        path,
        label: path,
        proofEligible: isProofPath(path),
      })),
      ...fallbackSources.map((source) => ({
        sourceType: source.type,
        path: `@fallback[${source.id}]`,
        label: source.label,
        proofEligible: source.type === 'proof',
      })),
    ],
    hydrated: hydratedData ?? undefined,
  };
}

function collectionToDesignForm(collection: AdCreativeDesignCollection): DesignCollectionForm {
  const styleAnchor = collection.styleAnchor ?? {};
  const referenceStrategy = collection.referenceStrategy ?? {};
  return {
    id: collection.id,
    name: collection.name,
    description: collection.description ?? '',
    isCurrent: collection.isCurrent === true || collection.status === 'current',
    palette: Array.isArray(styleAnchor.palette) ? styleAnchor.palette.join(', ') : String(styleAnchor.palette ?? ''),
    typography: String(styleAnchor.typography ?? ''),
    composition: String(styleAnchor.composition ?? ''),
    textPolicy: String(styleAnchor.textPolicy ?? ''),
    referencePolicy: Array.isArray(referenceStrategy.requiredRefs)
      ? referenceStrategy.requiredRefs.join(', ')
      : String(referenceStrategy.requiredRefs ?? referenceStrategy.sourcePolicy ?? ''),
    presets: (collection.presets ?? []).map((preset) => ({
      id: preset.id,
      creativeType: preset.creativeType,
      name: preset.name,
      description: preset.description ?? '',
      enabled: preset.status !== 'archived',
      templateIds: preset.templateIds ?? [],
      placementFamilies: preset.placementFamilies ?? [],
      placementIds: preset.placementIds ?? [],
      aspectRatios: preset.aspectRatios ?? [],
      slotLimitPerCreative: Number((preset.generationDefaults as any)?.slotLimitPerCreative ?? (preset.slotPolicies as any)?.slotLimitPerCreative ?? 1),
      sourceProfileKey: preset.sourceProfileKey ?? '',
      slotPolicies: recordValue(preset.slotPolicies),
      styleAnchor: recordValue(preset.styleAnchor),
      brandStyleKit: recordValue(preset.brandStyleKit),
      referenceStrategy: recordValue(preset.referenceStrategy),
      generationDefaults: recordValue(preset.generationDefaults),
      previewSpec: recordValue(preset.previewSpec),
    })),
  };
}

function formToSaveDesignRequest({
  projectId,
  networkId,
  form,
}: {
  projectId: string;
  networkId: string;
  form: DesignCollectionForm;
}): SaveAdCreativeDesignCollectionRequest {
  const palette = form.palette.split(',').map((entry) => entry.trim()).filter(Boolean);
  return {
    projectId,
    networkId,
    collectionId: form.id,
    name: form.name,
    description: form.description,
    isCurrent: form.isCurrent,
    styleAnchor: {
      styleName: form.name,
      palette,
      typography: form.typography,
      composition: form.composition,
      textPolicy: form.textPolicy,
    },
    referenceStrategy: {
      inheritance: 'collection_foundation_to_creative_type_preset',
      sourcePolicy: 'reference_only',
      requiredRefs: form.referencePolicy.split(',').map((entry) => entry.trim()).filter(Boolean),
    },
    presets: form.presets.map((preset) => ({
      id: preset.id,
      networkId: 'preview',
      creativeType: preset.creativeType,
      name: preset.name,
      description: preset.description,
      templateIds: preset.templateIds,
      placementFamilies: preset.placementFamilies,
      placementIds: preset.placementIds,
      aspectRatios: preset.aspectRatios,
      status: preset.enabled ? 'reviewed' : 'archived',
      sourceProfileKey: preset.sourceProfileKey,
      generationDefaults: {
        ...(preset.generationDefaults ?? {}),
        generateAssets: false,
        slotLimitPerCreative: preset.slotLimitPerCreative,
      },
      slotPolicies: {
        ...(preset.slotPolicies ?? {}),
        slotLimitPerCreative: preset.slotLimitPerCreative,
        overlayTextPolicy: 'template_controlled',
      },
      styleAnchor: {
        ...(preset.styleAnchor ?? {}),
        creativeType: preset.creativeType,
        composition: String(preset.styleAnchor?.composition ?? `${form.composition}; ${humanizeId(preset.creativeType)} placement adaptation`),
      },
      brandStyleKit: preset.brandStyleKit,
      referenceStrategy: {
        ...(preset.referenceStrategy ?? {}),
        inheritsFromCollection: true,
      },
      previewSpec: preset.previewSpec,
    })),
  };
}

function designFormPreview(form: DesignCollectionForm): AdCreativeDesignCollection {
  return {
    id: form.id || 'draft',
    networkId: 'preview',
    name: form.name,
    description: form.description,
    status: form.isCurrent ? 'current' : 'draft',
    styleAnchor: {
      palette: form.palette.split(',').map((entry) => entry.trim()).filter(Boolean),
      typography: form.typography,
      composition: form.composition,
      textPolicy: form.textPolicy,
    },
    referenceStrategy: {
      requiredRefs: form.referencePolicy.split(',').map((entry) => entry.trim()).filter(Boolean),
      sourcePolicy: 'reference_only',
    },
    presets: form.presets.map((preset) => ({
      id: preset.id,
      creativeType: preset.creativeType,
      name: preset.name,
      description: preset.description,
      templateIds: preset.templateIds,
      placementFamilies: preset.placementFamilies,
      placementIds: preset.placementIds,
      aspectRatios: preset.aspectRatios,
      status: preset.enabled ? 'reviewed' : 'archived',
      sourceProfileKey: preset.sourceProfileKey,
      generationDefaults: {
        ...(preset.generationDefaults ?? {}),
        generateAssets: false,
        slotLimitPerCreative: preset.slotLimitPerCreative,
      },
      slotPolicies: {
        ...(preset.slotPolicies ?? {}),
        slotLimitPerCreative: preset.slotLimitPerCreative,
      },
      styleAnchor: preset.styleAnchor,
      brandStyleKit: preset.brandStyleKit,
      referenceStrategy: preset.referenceStrategy,
      previewSpec: preset.previewSpec,
    })),
  } as unknown as AdCreativeDesignCollection;
}

function parseDesignReferenceNotes(value: string): Array<Record<string, unknown>> {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [label, ...rest] = line.split(':');
      const hasExplicitLabel = rest.length > 0 && label.trim().length > 0;
      return {
        id: `ref_${index + 1}`,
        kind: 'text_reference',
        label: hasExplicitLabel ? label.trim() : `Reference ${index + 1}`,
        description: hasExplicitLabel ? rest.join(':').trim() : line,
      };
    });
}

function extractIdsFromFallback(sources: SourceOption[]): Record<string, string[]> {
  return {
    audienceIds: sources.filter((source) => source.type === 'audience').map((source) => source.id),
    taskIds: sources.filter((source) => source.type === 'task').map((source) => source.id),
    insightIds: sources.filter((source) => source.type === 'insight').map((source) => source.id),
    reviewIds: sources.filter((source) => source.type === 'proof').map((source) => source.id),
    ratingIds: [],
    studyCaseIds: [],
    bigNumberIds: [],
  };
}

function extractIdsFromPaths(paths: string[]): Record<string, string[]> {
  return {
    audienceIds: extractEntityIds(paths, 'audience'),
    taskIds: extractEntityIds(paths, 'task'),
    insightIds: extractEntityIds(paths, 'insight'),
    reviewIds: extractEntityIds(paths, 'review'),
    ratingIds: extractEntityIds(paths, 'rating'),
    studyCaseIds: extractEntityIds(paths, 'studyCase'),
    bigNumberIds: extractEntityIds(paths, 'bigNumber'),
  };
}

function extractEntityIds(paths: string[], entity: string): string[] {
  const pattern = new RegExp(`@${entity}\\[([^\\]]+)\\]`);
  return uniqueStrings(paths.map((path) => path.match(pattern)?.[1]).filter(Boolean) as string[]);
}

function extractSourceType(path: string): string {
  return path.match(/^@([^\[]+)\[/)?.[1] ?? 'custom';
}

function isProofPath(path: string): boolean {
  return /^@(review|rating|studyCase|bigNumber)\[/.test(path);
}

function buildPresetMap(presets: AdCreativeDesignPreset[]): Record<string, string> {
  return presets.reduce<Record<string, string>>((acc, preset) => {
    acc[preset.creativeType] = preset.id;
    return acc;
  }, {});
}

function resolveSlotLimit(presets: AdCreativeDesignPreset[]): number {
  const limits = presets
    .map((preset) => Number((preset.generationDefaults as any)?.slotLimitPerCreative))
    .filter((limit) => Number.isFinite(limit) && limit > 0);
  return Math.max(1, ...limits, 1);
}

function mergeCampaigns(next: AdCreativeCampaign[], current: AdCreativeCampaign[]): AdCreativeCampaign[] {
  const byId = new Map<string, AdCreativeCampaign>();
  for (const campaign of [...next, ...current]) byId.set(campaign.id, campaign);
  return Array.from(byId.values());
}

function mergeBriefs(next: AdCreativeBrief[], current: AdCreativeBrief[]): AdCreativeBrief[] {
  const byId = new Map<string, AdCreativeBrief>();
  for (const brief of [...next, ...current]) byId.set(brief.id, brief);
  return Array.from(byId.values());
}

function creativeLibraryLoadMessage(error: unknown): string {
  const message = typeof error === 'string' ? error : '';
  if (/failed_precondition|requires an index/i.test(message)) {
    return 'The creative library is not ready in this environment yet. Retry after its data index is available.';
  }
  if (/failed to fetch|connection refused|network/i.test(message)) {
    return 'The creative service is temporarily unavailable. Please retry in a moment.';
  }
  return 'Please retry. If the problem continues, contact support.';
}

function mergeItems(next: AdCreativeItem[], current: AdCreativeItem[]): AdCreativeItem[] {
  const byId = new Map<string, AdCreativeItem>();
  for (const item of [...next, ...current]) byId.set(item.id, item);
  return Array.from(byId.values());
}

function mergeDesignCollections(
  next: AdCreativeDesignCollection[],
  current: AdCreativeDesignCollection[],
): AdCreativeDesignCollection[] {
  const byId = new Map<string, AdCreativeDesignCollection>();
  for (const collection of [...next, ...current]) byId.set(collection.id, collection);
  return Array.from(byId.values()).sort((left, right) => {
    if (left.isCurrent && !right.isCurrent) return -1;
    if (!left.isCurrent && right.isCurrent) return 1;
    return left.name.localeCompare(right.name);
  });
}

function adCreativeTaskToBackendTask(task: AdCreativeTaskDocument, projectId: string): BackendTask {
  const record = task as unknown as Record<string, unknown>;
  return {
    ...record,
    id: task.id,
    action: String(record.action ?? task.taskType ?? 'ad-creative-task'),
    taskType: task.taskType as BackendTask['taskType'],
    status: task.status as BackendTask['status'],
    projectId: String(record.projectId ?? projectId),
    userId: typeof record.userId === 'string' ? record.userId : undefined,
    description: typeof record.description === 'string' ? record.description : undefined,
    provider: typeof record.provider === 'string' ? record.provider : undefined,
    model: typeof record.model === 'string' ? record.model : undefined,
    phases: task.phases as BackendTask['phases'],
    currentPhaseKey: task.currentPhaseKey,
    targetEntityPath: record.targetEntityPath as BackendTask['targetEntityPath'],
    result: record.result as BackendTask['result'],
    error: typeof record.error === 'string' ? record.error : undefined,
    viewedBy: Array.isArray(record.viewedBy) ? record.viewedBy.map(String) : [],
    createdAt: normalizeTaskDate(record.createdAt) ?? new Date(),
    updatedAt: normalizeTaskDate(record.updatedAt),
  } as BackendTask;
}

function getTaskArtifacts(task: BackendTask): Record<string, unknown> {
  const artifacts = (task as unknown as { artifacts?: unknown }).artifacts;
  return artifacts && typeof artifacts === 'object' && !Array.isArray(artifacts)
    ? artifacts as Record<string, unknown>
    : {};
}

function briefFieldText(value: unknown, key: string): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const raw = (value as Record<string, unknown>)[key];
  return raw === undefined || raw === null ? '' : String(raw);
}

function formatAdCreativeDate(value: unknown): string {
  const date = normalizeTaskDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
}

function normalizeTaskDate(value: unknown): Date | undefined {
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? new Date(parsed) : undefined;
  }
  if (value && typeof value === 'object') {
    if ('toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
      const date = (value as { toDate: () => Date }).toDate();
      return date instanceof Date ? date : undefined;
    }
    const seconds = (value as { _seconds?: unknown; seconds?: unknown })._seconds
      ?? (value as { seconds?: unknown }).seconds;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000);
    }
  }
  return undefined;
}

function artifactString(artifacts: Record<string, unknown>, key: string): string | null {
  const value = artifacts[key];
  const text = value === undefined || value === null ? '' : String(value).trim();
  return text || null;
}

function getFirstExperiment(item: AdCreativeItem): AiAssetExperiment | undefined {
  const aiAssets = item.aiAssets as Record<string, { selectedExperimentId?: string; experiments?: Record<string, AiAssetExperiment> }> | undefined;
  if (!aiAssets) return undefined;
  for (const slot of Object.values(aiAssets)) {
    const selected = slot.selectedExperimentId ? slot.experiments?.[slot.selectedExperimentId] : undefined;
    if (selected) return selected;
    const first = Object.values(slot.experiments ?? {})[0];
    if (first) return first;
  }
  return undefined;
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  return Array.from(new Set(values.filter(Boolean) as string[]));
}

function formatList(value: unknown): string {
  return Array.isArray(value) ? value.map(String).join(', ') : String(value || '');
}

function textToList(value: string): string[] {
  return value.split(',').map((entry) => entry.trim()).filter(Boolean);
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {};
}

function recordSummary(value: unknown): string {
  const record = recordValue(value);
  const keys = Object.keys(record);
  return keys.length > 0 ? keys.join(', ') : 'Not configured';
}

function humanizeId(value: string | undefined): string {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
